// src/pages/team/TeamMembersPage.jsx
// Extracted from App.js - Team Members admin page
// BRIO Migration: Table listing code "1a", Edit modal table code "1b"
// Edit Team Member modal redesigned as compact form-as-table layout
//
// Option A: Auth user created in Supabase dashboard, linked here via admin modal
// This allows admins to link Supabase Auth UUIDs to employee rows in public.users
//
import React, { useState, useCallback, useEffect } from 'react';
import { Plus, Link, AlertTriangle, Key, UserPlus, Maximize2, Pencil, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import {
  BrioAnalyticsSurface,
  BrioByCode,
  BrioTable,
  BrioUserIcon,
  BrioIconButton,
  BrioActionGroup,
  BrioFrame,
  BrioModal,
  getTablePropsFromCode,
  BrioDatePicker
} from '../../components/BrioComponents';
import { supabase } from '../../supabaseClient';
import { adminCreateUser, adminResetPassword, normalizedIncludes, normalizeId } from '../../hooks/useSupabase';
import { useUserRoleHistory } from '../../hooks/useUserRoleHistory';
import TeamMemberFullScreenPage from './TeamMemberFullScreenPage';

// Presentation codes
const PRESENTATION_CODE = '1a';
const MODAL_TABLE_CODE = '1b';

// Pages that admins can grant non-admin/non-supervisor users temporary access to.
// Mirrored by RLS policies in supabase/migrations/20260511000001_user_temp_page_access.sql
// (Contacts Directory) and 20260511000002_temp_access_hiring_and_settings.sql
// (Hiring, Accounting Settings). canAccessPage() in App.js consumes the same keys.
// Add new entries here to extend the feature.
const TEMP_ACCESS_PAGE_OPTIONS = [
  { key: 'contacts-directory', label: 'Contacts Directory' },
  { key: 'comms', label: 'Comms & Outreach (full access)' },
  { key: 'hiring', label: 'Hiring (supervisor-level — current candidates only)' },
  { key: 'settings', label: 'Accounting Settings (full write)' },
];

// Convert a YYYY-MM-DD date input value to an end-of-day ISO timestamp (local time).
// Returns null for empty/invalid input.
function tempAccessDateToIso(dateStr) {
  if (!dateStr) return null;
  const d = new Date(`${dateStr}T23:59:59`);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

// Convert a stored ISO timestamp back to YYYY-MM-DD for the date input.
function tempAccessIsoToDateInput(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

// Job roles for NL (Rotterdam) users — one entry per schaal (1–10),
// matching the first column of the NL Salary Matrix (SalaryMatrixNLPage).
// Custom rows added in the salary matrix are appended dynamically at runtime.
const NL_JOB_ROLES_BASE = [
  'Intern (1)',
  'Junior Architect (2)',
  'Junior Architect (3)',
  'Medior Architect (4)',
  'Medior Architect (5)',
  'Project Architect (6)',
  'Senior Architect (7)',
  'Senior Architect (8)',
  'Senior Architect (9)',
  'Associate Architect (10)',
];

// Extract NL schaal from role name: "Senior Architect (7)" → "7", "c_1" → "c_1"
function extractNlSchaal(roleName) {
  if (!roleName) return null;
  if (/^(c_)?[0-9]{1,2}$/.test(roleName)) return roleName;
  const m = roleName.match(/\((\d{1,2})\)\s*$/);
  return m ? m[1] : null;
}

// UUID validation regex (standard UUID v4 format)
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

// ── Shared input style (matches ManageProjectsPage modal) ───────────
const INPUT_STYLE = {
  width: '100%',
  padding: '6px 10px',
  border: '1px solid var(--brio-border, #d1d5db)',
  borderRadius: '6px',
  fontSize: '13px',
  lineHeight: '1.4',
  background: '#fff',
  color: 'var(--brio-text, #1e293b)',
  boxSizing: 'border-box',
};

const SELECT_STYLE = { ...INPUT_STYLE, appearance: 'auto' };

const HINT_STYLE = {
  fontSize: '11px',
  color: 'var(--brio-muted, #94a3b8)',
  marginTop: '2px',
  lineHeight: '1.3',
  whiteSpace: 'normal',
};

// ── Reusable table-row component ────────────────────────────────────
function FormRow({ label, hint, children, sectionHeader }) {
  if (sectionHeader) {
    return (
      <tr>
        <td colSpan="2" style={{
          padding: '10px 12px 6px',
          fontWeight: 600,
          fontSize: '12px',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          color: 'var(--brio-gold, #9F902A)',
          borderBottom: '1px solid var(--brio-border, #e2e8f0)',
          background: 'var(--brio-bg-subtle, #fafaf6)',
        }}>
          {label}
        </td>
      </tr>
    );
  }
  return (
    <tr>
      <td style={{
        padding: '8px 12px',
        verticalAlign: 'top',
        whiteSpace: 'nowrap',
        fontWeight: 500,
        fontSize: '13px',
        color: 'var(--brio-text, #475569)',
        width: '160px',
        minWidth: '130px',
      }}>
        {label}
        {hint && <div style={HINT_STYLE}>{hint}</div>}
      </td>
      <td style={{
        padding: '6px 12px',
        verticalAlign: 'top',
      }}>
        {children}
      </td>
    </tr>
  );
}

// ============================================================================
// HELPER: Get auth_user_id from user object (handles both camelCase and snake_case)
// ============================================================================
const getAuthUserId = (user) => {
  if (!user) return '';
  return user.authUserId ?? user.auth_user_id ?? '';
};

// ============================================================================
// ROLE HISTORY EDITOR COMPONENT
// ============================================================================
// Inline component for the Edit Team Member modal.
// Uses useUserRoleHistory hook for CRUD, displays timeline table with
// add/edit/delete, gap warnings, and backfill button.
// ============================================================================

const RH_STYLES = {
  table: {
    width: '100%',
    fontSize: '12px',
    borderCollapse: 'collapse',
    marginTop: '4px',
  },
  // AUDIT: fontWeight 600 not a BRIO token → use 500 or 700
  th: {
    padding: '6px 8px',
    textAlign: 'left',
    fontWeight: 600,
    fontSize: '11px',
    color: 'var(--brio-muted, #64748b)',
    borderBottom: '2px solid var(--brio-border, #e2e8f0)',
    whiteSpace: 'nowrap',
  },
  td: {
    padding: '5px 8px',
    borderBottom: '1px solid var(--brio-border, #f1f5f9)',
    verticalAlign: 'middle',
    fontSize: '12px',
  },
  badge: (color) => ({
    display: 'inline-block',
    padding: '1px 6px',
    borderRadius: '4px',
    fontSize: '10px',
    fontWeight: 500,
    background: color === 'green' ? '#dcfce7' : color === 'amber' ? '#fef3c7' : color === 'red' ? '#fef2f2' : '#f1f5f9',
    color: color === 'green' ? '#166534' : color === 'amber' ? '#92400e' : color === 'red' ? '#991b1b' : '#475569',
  }),
  warning: {
    background: '#fefce8',
    border: '1px solid #fde68a',
    borderRadius: '6px',
    padding: '8px 12px',
    fontSize: '12px',
    color: '#854d0e',
    marginTop: '6px',
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '8px',
    marginTop: '8px',
  },
  formField: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  formLabel: {
    fontSize: '11px',
    fontWeight: 500,
    color: 'var(--brio-muted, #64748b)',
  },
};

function RoleHistoryEditor({
  userId,
  user,
  jobRoles = [],
  nlCustomRoles = [],
  nlCustomRoleSchaalMap = {},
  nlCustomTredes = [],
  isAdmin,
  activeProjects = [],
  activeTasks = [],
}) {
  const {
    roleHistory,
    loading,
    error,
    refetch,
    addRoleHistoryRow,
    updateRoleHistoryRow,
    deleteRoleHistoryRow,
    gaps,
    hasCurrentRow,
  } = useUserRoleHistory(userId);

  const [showForm, setShowForm] = useState(false);
  const [editingRow, setEditingRow] = useState(null);  // null = adding, object = editing
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [expanded, setExpanded] = useState(true);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  // Backfill confirmation modal state. payload = {preview, payload, savedRowId?}
  const [backfillConfirm, setBackfillConfirm] = useState(null);

  const resetForm = () => {
    setShowForm(false);
    setEditingRow(null);
    setFormData({});
    setFormError(null);
  };

  const openAddForm = () => {
    setEditingRow(null);
    const role = user?.jobRole || '';
    setFormData({
      effectiveFrom: new Date().toISOString().split('T')[0],
      effectiveTo: '',
      jobRole: role,
      jobLevel: user?.jobLevel ?? 0,
      officeLocation: user?.officeLocation || 'NL',
      contractedHours: 176,
      loggingMode: 'self',
      autoAllocatedProjectId: null,
      autoAllocatedTaskId: null,
      source: 'manual',
      confidence: 'high',
      notes: '',
      nlSchaal: extractNlSchaal(role),
      nlTrede: user?.jobLevel ?? 0,
    });
    setFormError(null);
    setShowForm(true);
  };

  const openEditForm = (row) => {
    setEditingRow(row);
    setFormData({
      effectiveFrom: row.effectiveFrom || '',
      effectiveTo: row.effectiveTo || '',
      jobRole: row.jobRole || '',
      jobLevel: row.jobLevel ?? 0,
      officeLocation: row.officeLocation || 'NL',
      contractedHours: row.contractedHours ?? 176,
      loggingMode: row.loggingMode || (row.isFreelancer ? 'freelancer' : 'self'),
      autoAllocatedProjectId: row.autoAllocatedProjectId ?? null,
      autoAllocatedTaskId: row.autoAllocatedTaskId ?? null,
      source: row.source || 'manual',
      confidence: row.confidence || 'high',
      notes: row.notes || '',
      nlSchaal: row.nlSchaal || extractNlSchaal(row.jobRole),
      nlTrede: row.nlTrede ?? row.jobLevel ?? 0,
    });
    setFormError(null);
    setShowForm(true);
  };

  // Save flow:
  // 1. Validate.
  // 2. If logging mode (new or old) is auto_allocated, fetch a preview
  //    and show the backfill confirmation modal. Save+fill happens on
  //    confirm; cancel leaves the form open without saving.
  // 3. Otherwise, save directly and call fill (which cleans up any leftover
  //    auto entries from a prior auto_allocated state).
  const buildPayload = () => {
    const isNL = formData.officeLocation === 'NL';
    return {
      userId,
      effectiveFrom: formData.effectiveFrom,
      effectiveTo: formData.effectiveTo || null,
      jobRole: formData.jobRole,
      jobLevel: parseInt(formData.jobLevel) || 0,
      officeLocation: formData.officeLocation || null,
      contractedHours: parseInt(formData.contractedHours) || 176,
      loggingMode: formData.loggingMode || 'self',
      autoAllocatedProjectId: formData.loggingMode === 'auto_allocated'
        ? (formData.autoAllocatedProjectId || null) : null,
      autoAllocatedTaskId: formData.loggingMode === 'auto_allocated'
        ? (formData.autoAllocatedTaskId || null) : null,
      source: formData.source || 'manual',
      confidence: formData.confidence || 'high',
      notes: formData.notes || null,
      nlSchaal: isNL ? (formData.nlSchaal || extractNlSchaal(formData.jobRole) || nlCustomRoleSchaalMap[formData.jobRole] || null) : null,
      nlTrede: isNL ? (parseInt(formData.jobLevel) || 0) : null,
    };
  };

  const commitSaveAndFill = async (payload, runFill, replaceManual = false) => {
    let result;
    if (editingRow) {
      result = await updateRoleHistoryRow(editingRow.id, payload);
    } else {
      result = await addRoleHistoryRow(payload);
    }
    if (!result.success) {
      return result;
    }
    const rowId = editingRow ? editingRow.id : result.id;
    if (runFill && rowId) {
      const { error: fillError } = await supabase.rpc('fill_auto_allocated_for_row', {
        p_row_id:         rowId,
        p_replace_manual: replaceManual === true,
      });
      if (fillError) {
        return { success: false, error: 'Saved, but auto-fill failed: ' + fillError.message };
      }
    }
    return { success: true };
  };

  const handleSave = async () => {
    if (!formData.effectiveFrom || !formData.jobRole) {
      setFormError('Effective From and Job Role are required.');
      return;
    }
    if (formData.loggingMode === 'auto_allocated') {
      if (!formData.autoAllocatedProjectId) {
        setFormError('Auto-allocated mode requires a project.');
        return;
      }
      if (!formData.autoAllocatedTaskId) {
        setFormError('Auto-allocated mode requires a task.');
        return;
      }
    }

    setSaving(true);
    setFormError(null);

    const payload = buildPayload();
    const wasAutoAllocated = editingRow?.loggingMode === 'auto_allocated';
    const isAutoAllocated  = payload.loggingMode === 'auto_allocated';
    const needsAutoFlow    = wasAutoAllocated || isAutoAllocated;

    if (!needsAutoFlow) {
      const result = await commitSaveAndFill(payload, false);
      setSaving(false);
      if (!result.success) {
        setFormError(result.error || 'Operation failed.');
      } else {
        resetForm();
      }
      return;
    }

    // Preview the backfill (server-side via params RPC).
    let preview = null;
    try {
      const { data, error: previewError } = await supabase.rpc('fill_auto_allocated_preview_params', {
        p_user_id:          userId,
        p_effective_from:   payload.effectiveFrom,
        p_effective_to:     payload.effectiveTo,
        p_contracted_hours: payload.contractedHours,
        p_exclude_row_id:   editingRow?.id ?? null,
      });
      if (previewError) throw previewError;
      preview = Array.isArray(data) ? data[0] : data;
    } catch (err) {
      setSaving(false);
      setFormError('Could not preview backfill: ' + (err.message || 'unknown error'));
      return;
    }

    setSaving(false);

    // If transitioning auto → non-auto, we still want to confirm the cleanup
    // (so admins know existing auto entries will be deleted).
    setBackfillConfirm({
      preview,
      payload,
      mode: isAutoAllocated ? 'fill' : 'cleanup',
      existingAutoCount: editingRow?.id ? null : 0,   // resolved below for edits
      replaceManual: false,                            // user can opt in via checkbox
    });

    // Look up the existing auto count for editing the row (so the modal
    // can show "delete X existing"). Fire-and-forget; if it errors, the
    // modal still works.
    if (editingRow?.id) {
      supabase
        .from('time_entries')
        .select('id', { count: 'exact', head: true })
        .eq('auto_role_history_id', editingRow.id)
        .then(({ count }) => {
          setBackfillConfirm(cur => cur ? { ...cur, existingAutoCount: count ?? 0 } : cur);
        });
    }
  };

  const confirmBackfill = async () => {
    if (!backfillConfirm) return;
    setSaving(true);
    setFormError(null);
    const result = await commitSaveAndFill(
      backfillConfirm.payload,
      true /* always run fill so old auto entries are cleaned up */,
      backfillConfirm.replaceManual === true
    );
    setSaving(false);
    if (!result.success) {
      setFormError(result.error || 'Operation failed.');
      setBackfillConfirm(null);
      return;
    }
    setBackfillConfirm(null);
    resetForm();
  };

  const cancelBackfill = () => setBackfillConfirm(null);

  const handleDelete = async (id) => {
    setSaving(true);
    const result = await deleteRoleHistoryRow(id);
    setSaving(false);
    setConfirmDeleteId(null);
    if (!result.success) {
      alert('Delete failed: ' + (result.error || 'Unknown error'));
    }
  };

  // Backfill: create one row from join date with current snapshot data
  const handleBackfill = async () => {
    if (roleHistory.length > 0) return;
    const joinDate = user?.joinDate || user?.join_date;
    const fromDate = joinDate || (user?.createdAt || new Date().toISOString()).split('T')[0];
    setSaving(true);
    const result = await addRoleHistoryRow({
      userId,
      effectiveFrom: fromDate,
      effectiveTo: null,
      jobRole: user?.jobRole || 'Architect',
      jobLevel: user?.jobLevel ?? 0,
      officeLocation: user?.officeLocation || 'NL',
      contractedHours: 176,
      source: 'default_backfill',
      confidence: 'low',
      notes: 'Auto-backfilled from current profile snapshot',
    });
    setSaving(false);
    if (!result.success) {
      alert('Backfill failed: ' + (result.error || 'Unknown error'));
    }
  };

  return (
    <>
      {/* Section Header */}
      <tr>
        <td colSpan="2" style={{
          padding: '10px 12px 6px',
          fontWeight: 600,
          fontSize: '12px',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          color: 'var(--brio-gold, #9F902A)',
          borderBottom: '1px solid var(--brio-border, #e2e8f0)',
          background: 'var(--brio-bg-subtle, #fafaf6)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <button
              onClick={() => setExpanded(!expanded)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', padding: 0, font: 'inherit', color: 'inherit', textTransform: 'inherit', letterSpacing: 'inherit', fontWeight: 'inherit', fontSize: 'inherit' }}
            >
              {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              Role History
              <span style={{ fontWeight: 400, fontSize: '11px', textTransform: 'none', letterSpacing: 0, color: 'var(--brio-muted, #94a3b8)' }}>
                ({roleHistory.length} {roleHistory.length === 1 ? 'entry' : 'entries'})
              </span>
            </button>
            {expanded && isAdmin && (
              <div style={{ display: 'flex', gap: '6px' }}>
                {roleHistory.length === 0 && (
                  <button
                    onClick={handleBackfill}
                    disabled={saving}
                    style={{
                      fontSize: '11px', padding: '2px 8px', borderRadius: '4px',
                      background: '#f0f9ff', border: '1px solid #bae6fd', color: '#0369a1',
                      cursor: 'pointer', fontWeight: 500, textTransform: 'none', letterSpacing: 0,
                    }}
                  >
                    Backfill from snapshot
                  </button>
                )}
                <button
                  onClick={openAddForm}
                  disabled={saving}
                  style={{
                    fontSize: '11px', padding: '2px 8px', borderRadius: '4px',
                    background: 'var(--brio-gold, #9F902A)', border: 'none', color: '#fff',
                    cursor: 'pointer', fontWeight: 500, textTransform: 'none', letterSpacing: 0,
                  }}
                >
                  + Add role change
                </button>
              </div>
            )}
          </div>
        </td>
      </tr>

      {expanded && (
        <tr>
          <td colSpan="2" style={{ padding: '8px 12px' }}>
            {/* Loading / Error */}
            {loading && <div style={{ fontSize: '12px', color: 'var(--brio-muted)' }}>Loading role history…</div>}
            {error && <div style={{ ...RH_STYLES.warning, background: '#fef2f2', borderColor: '#fecaca', color: '#991b1b' }}>Error: {error}</div>}

            {/* Warnings */}
            {/* Only warn about missing current role for active employees — past employees with a closed role are expected */}
            {!loading && !hasCurrentRow && roleHistory.length > 0 && user?.isActive !== false && (
              <div style={RH_STYLES.warning}>
                ⚠ No current role set. The most recent closed entry is used as snapshot.
              </div>
            )}
            {!loading && gaps.length > 0 && (
              <div style={RH_STYLES.warning}>
                ⚠ Coverage gaps detected:
                {gaps.map((g, i) => (
                  <div key={i} style={{ marginLeft: '12px' }}>• {g.from} → {g.to}</div>
                ))}
              </div>
            )}

            {/* Timeline table */}
            {!loading && roleHistory.length > 0 && (
              <table style={RH_STYLES.table}>
                <thead>
                  <tr>
                    <th style={RH_STYLES.th}>From</th>
                    <th style={RH_STYLES.th}>To</th>
                    <th style={RH_STYLES.th}>Role</th>
                    <th style={RH_STYLES.th}>Lvl</th>
                    <th style={RH_STYLES.th}>Office</th>
                    <th style={{ ...RH_STYLES.th, textAlign: 'right' }}>Hours</th>
                    <th style={RH_STYLES.th}>Source</th>
                    {isAdmin && <th style={{ ...RH_STYLES.th, textAlign: 'right' }}>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {roleHistory.map(row => (
                    <tr key={row.id} style={{ background: row.effectiveTo === null ? '#fafaf6' : 'transparent' }}>
                      <td style={RH_STYLES.td}>{row.effectiveFrom}</td>
                      <td style={RH_STYLES.td}>
                        {row.effectiveTo || <span style={RH_STYLES.badge('green')}>Current</span>}
                      </td>
                      <td style={RH_STYLES.td}>
                        {row.jobRole}
                        {row.loggingMode === 'freelancer' && (
                          <span style={{ ...RH_STYLES.badge('amber'), marginLeft: '6px' }}>Freelancer</span>
                        )}
                        {row.loggingMode === 'auto_allocated' && (
                          <span style={{ ...RH_STYLES.badge('green'), marginLeft: '6px' }}>
                            Auto → {(activeProjects || []).find(p => p.id === row.autoAllocatedProjectId)?.name || '?'}
                          </span>
                        )}
                      </td>
                      <td style={RH_STYLES.td}>{(() => {
                        const ct = nlCustomTredes.find(t => String(t.id) === String(row.jobLevel));
                        return ct ? ct.name : row.jobLevel;
                      })()}</td>
                      <td style={RH_STYLES.td}>{row.officeLocation || '—'}</td>
                      <td style={{ ...RH_STYLES.td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                        {row.contractedHours ?? 176}
                      </td>
                      <td style={RH_STYLES.td}>
                        <span style={RH_STYLES.badge(
                          row.confidence === 'high' ? 'green' : row.confidence === 'medium' ? 'amber' : 'red'
                        )}>
                          {row.source}
                        </span>
                      </td>
                      {isAdmin && (
                        <td style={{ ...RH_STYLES.td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                          {confirmDeleteId === row.id ? (
                            <span style={{ fontSize: '11px' }}>
                              Delete?{' '}
                              <button
                                onClick={() => handleDelete(row.id)}
                                disabled={saving}
                                style={{ color: '#dc2626', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer', fontSize: '11px' }}
                              >
                                Yes
                              </button>
                              {' / '}
                              <button
                                onClick={() => setConfirmDeleteId(null)}
                                style={{ color: '#64748b', background: 'none', border: 'none', cursor: 'pointer', fontSize: '11px' }}
                              >
                                No
                              </button>
                            </span>
                          ) : (
                            <>
                              <button
                                onClick={() => openEditForm(row)}
                                title="Edit"
                                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px', color: 'var(--brio-gold, #9F902A)' }}
                              >
                                <Pencil size={13} />
                              </button>
                              <button
                                onClick={() => setConfirmDeleteId(row.id)}
                                title="Delete"
                                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px', color: '#dc2626' }}
                              >
                                <Trash2 size={13} />
                              </button>
                            </>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {!loading && roleHistory.length === 0 && !error && (
              <div style={{ fontSize: '12px', color: 'var(--brio-muted, #94a3b8)', fontStyle: 'italic', padding: '4px 0' }}>
                No role history entries yet.
                {isAdmin && ' Use "Backfill from snapshot" or "+ Add role change" to start.'}
              </div>
            )}

            {/* Inline add/edit form */}
            {showForm && isAdmin && (
              <div style={{
                marginTop: '8px',
                padding: '12px',
                background: '#fafaf6',
                border: '1px solid var(--brio-border, #e2e8f0)',
                borderRadius: '8px',
              }}>
                <div style={{ fontSize: '12px', fontWeight: 600, marginBottom: '6px', color: 'var(--brio-text, #1e293b)' }}>
                  {editingRow ? 'Edit Role Entry' : 'Add Role Change'}
                </div>

                {formError && (
                  <div style={{ ...RH_STYLES.warning, background: '#fef2f2', borderColor: '#fecaca', color: '#991b1b', marginTop: 0, marginBottom: '8px' }}>
                    {formError}
                  </div>
                )}

                <div style={RH_STYLES.formGrid}>
                  <div style={RH_STYLES.formField}>
                    <label style={RH_STYLES.formLabel}>From *</label>
                    <BrioDatePicker
                      value={formData.effectiveFrom || ''}
                      onChange={v => setFormData({ ...formData, effectiveFrom: v })}
                    />
                  </div>
                  <div style={RH_STYLES.formField}>
                    <label style={RH_STYLES.formLabel}>To (empty = current)</label>
                    <BrioDatePicker
                      value={formData.effectiveTo || ''}
                      onChange={v => setFormData({ ...formData, effectiveTo: v })}
                    />
                  </div>
                  <div style={RH_STYLES.formField}>
                    <label style={RH_STYLES.formLabel}>Job Role *</label>
                    <select
                      value={formData.jobRole || ''}
                      onChange={e => {
                        const role = e.target.value;
                        // For custom roles, look up schaal ID from the map (e.g. "Director" → "c_1")
                        const schaal = extractNlSchaal(role) || nlCustomRoleSchaalMap[role] || null;
                        setFormData({ ...formData, jobRole: role, nlSchaal: schaal });
                      }}
                      style={{ ...SELECT_STYLE, fontSize: '12px' }}
                    >
                      <option value="">Select Role</option>
                      {(formData.officeLocation === 'NL' ? [...NL_JOB_ROLES_BASE, ...nlCustomRoles] : jobRoles).map(jr => (
                        <option key={jr} value={jr}>{jr}</option>
                      ))}
                    </select>
                  </div>
                  <div style={RH_STYLES.formField}>
                    <label style={RH_STYLES.formLabel}>{formData.officeLocation === 'NL' ? 'Trede' : 'Level'}</label>
                    <select
                      value={formData.jobLevel ?? ''}
                      onChange={e => {
                        const level = parseInt(e.target.value) || 0;
                        setFormData({ ...formData, jobLevel: level, nlTrede: formData.officeLocation === 'NL' ? level : formData.nlTrede });
                      }}
                      style={{ ...SELECT_STYLE, fontSize: '12px' }}
                    >
                      {formData.officeLocation === 'NL'
                        ? <>
                            {Array.from({ length: 10 }, (_, i) => (
                              <option key={i} value={i}>Trede {i}</option>
                            ))}
                            {nlCustomTredes.map(ct => (
                              <option key={ct.id} value={ct.id}>{ct.name || `Trede ${ct.id}`}</option>
                            ))}
                          </>
                        : Array.from({ length: 21 }, (_, i) => (
                            <option key={i} value={i}>Level {i}</option>
                          ))
                      }
                    </select>
                  </div>
                  <div style={RH_STYLES.formField}>
                    <label style={RH_STYLES.formLabel}>Office</label>
                    <select
                      value={formData.officeLocation || 'NL'}
                      onChange={e => setFormData({ ...formData, officeLocation: e.target.value })}
                      style={{ ...SELECT_STYLE, fontSize: '12px' }}
                    >
                      <option value="NL">NL</option>
                      <option value="IN">IN</option>
                    </select>
                  </div>
                  <div style={RH_STYLES.formField}>
                    <label style={RH_STYLES.formLabel}>Contracted Hours</label>
                    <input
                      type="number"
                      min="0"
                      max="300"
                      value={formData.contractedHours ?? 176}
                      onChange={e => setFormData({ ...formData, contractedHours: parseInt(e.target.value) || 176 })}
                      style={{ ...INPUT_STYLE, fontSize: '12px', width: '100px' }}
                    />
                  </div>
                  <div style={RH_STYLES.formField}>
                    <label style={RH_STYLES.formLabel}>Logging mode</label>
                    <select
                      value={formData.loggingMode || 'self'}
                      onChange={e => setFormData({ ...formData, loggingMode: e.target.value })}
                      style={{ ...SELECT_STYLE, fontSize: '12px' }}
                    >
                      <option value="self">Self — user logs their own time</option>
                      <option value="freelancer">Freelancer — monthly hours approved separately</option>
                      <option value="auto_allocated">Auto-allocated to project (no user logging)</option>
                    </select>
                  </div>
                  {formData.loggingMode === 'auto_allocated' && (
                    <>
                      <div style={RH_STYLES.formField}>
                        <label style={RH_STYLES.formLabel}>Auto-allocated project *</label>
                        <select
                          value={formData.autoAllocatedProjectId ?? ''}
                          onChange={e => setFormData({ ...formData, autoAllocatedProjectId: e.target.value ? Number(e.target.value) : null })}
                          style={{ ...SELECT_STYLE, fontSize: '12px' }}
                        >
                          <option value="">Select project</option>
                          {[...(activeProjects || [])]
                            .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                            .map(p => (
                              <option key={p.id} value={p.id}>{p.name}</option>
                            ))}
                        </select>
                      </div>
                      <div style={RH_STYLES.formField}>
                        <label style={RH_STYLES.formLabel}>Auto-allocated task *</label>
                        <select
                          value={formData.autoAllocatedTaskId ?? ''}
                          onChange={e => setFormData({ ...formData, autoAllocatedTaskId: e.target.value ? Number(e.target.value) : null })}
                          style={{ ...SELECT_STYLE, fontSize: '12px' }}
                        >
                          <option value="">Select task</option>
                          {[...(activeTasks || [])]
                            .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                            .map(t => (
                              <option key={t.id} value={t.id}>{t.name}</option>
                            ))}
                        </select>
                      </div>
                    </>
                  )}
                  <div style={RH_STYLES.formField}>
                    <label style={RH_STYLES.formLabel}>Source</label>
                    <select
                      value={formData.source || 'manual'}
                      onChange={e => setFormData({ ...formData, source: e.target.value })}
                      style={{ ...SELECT_STYLE, fontSize: '12px' }}
                    >
                      <option value="manual">Manual</option>
                      <option value="hr_import">HR Import</option>
                      <option value="inferred">Inferred</option>
                      <option value="initial">Initial</option>
                    </select>
                  </div>
                  <div style={RH_STYLES.formField}>
                    <label style={RH_STYLES.formLabel}>Confidence</label>
                    <select
                      value={formData.confidence || 'high'}
                      onChange={e => setFormData({ ...formData, confidence: e.target.value })}
                      style={{ ...SELECT_STYLE, fontSize: '12px' }}
                    >
                      <option value="high">High</option>
                      <option value="medium">Medium</option>
                      <option value="low">Low</option>
                    </select>
                  </div>
                  <div style={{ ...RH_STYLES.formField, gridColumn: '1 / -1' }}>
                    <label style={RH_STYLES.formLabel}>Notes</label>
                    <input
                      type="text"
                      value={formData.notes || ''}
                      onChange={e => setFormData({ ...formData, notes: e.target.value })}
                      placeholder="e.g. Promotion, Transfer, etc."
                      style={{ ...INPUT_STYLE, fontSize: '12px' }}
                    />
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '8px', marginTop: '10px', justifyContent: 'flex-end' }}>
                  <button
                    onClick={resetForm}
                    disabled={saving}
                    style={{
                      fontSize: '12px', padding: '4px 12px', borderRadius: '4px',
                      background: '#fff', border: '1px solid var(--brio-border, #d1d5db)',
                      color: 'var(--brio-text, #475569)', cursor: 'pointer',
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    style={{
                      fontSize: '12px', padding: '4px 12px', borderRadius: '4px',
                      background: 'var(--brio-gold, #9F902A)', border: 'none', color: '#fff',
                      cursor: 'pointer', fontWeight: 600,
                      opacity: saving ? 0.6 : 1,
                    }}
                  >
                    {saving ? 'Saving…' : (editingRow ? 'Update' : 'Add')}
                  </button>
                </div>
              </div>
            )}
          </td>
        </tr>
      )}

      {/* Backfill confirmation modal — admin must confirm or cancel before save commits */}
      {backfillConfirm && (
        <tr>
          <td colSpan="2" style={{ padding: 0 }}>
            <div style={{
              position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.45)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
            }}>
              <div style={{
                background: '#fff', borderRadius: '10px', padding: '20px',
                maxWidth: '520px', width: '90%', boxShadow: '0 20px 50px rgba(15,23,42,0.25)',
                fontFamily: 'inherit',
              }}>
                <div style={{ fontSize: '14px', fontWeight: 700, marginBottom: '8px', color: 'var(--brio-text, #1e293b)' }}>
                  {backfillConfirm.mode === 'cleanup'
                    ? 'Stop auto-allocation?'
                    : 'Backfill auto-allocated time entries?'}
                </div>
                <div style={{ fontSize: '13px', color: 'var(--brio-text, #475569)', lineHeight: 1.5 }}>
                  {(() => {
                    const p = backfillConfirm.preview || {};
                    const project = (activeProjects || []).find(x => x.id === Number(backfillConfirm.payload.autoAllocatedProjectId));
                    const task = (activeTasks || []).find(x => x.id === Number(backfillConfirm.payload.autoAllocatedTaskId));
                    const existing = backfillConfirm.existingAutoCount;
                    if (backfillConfirm.mode === 'cleanup') {
                      return (
                        <>
                          This will <strong>delete {existing ?? '…'} existing auto-allocated time entries</strong> for this role-history row and stop generating new ones going forward.
                        </>
                      );
                    }
                    return (
                      <>
                        <div style={{ marginBottom: '10px' }}>
                          From <strong>{p.from_date}</strong> to <strong>{p.to_date}</strong> against <strong>{project?.name || '?'}</strong> · <strong>{task?.name || '?'}</strong>.
                        </div>
                        <div style={{ marginBottom: '10px' }}>
                          Will create <strong>{p.working_days ?? 0}</strong> daily entries totalling <strong>{Number(p.total_hours || 0).toFixed(1)}h</strong>{' '}
                          ({Number(p.daily_hours || 0).toFixed(2)}h per working day, skipping weekends, public holidays and approved leave).
                        </div>
                        {existing != null && existing > 0 && (
                          <div style={{ marginBottom: '10px' }}>
                            Replaces <strong>{existing}</strong> existing auto entries for this row.
                          </div>
                        )}
                        {Number(p.existing_manual || 0) > 0 && (
                          <div style={{
                            padding: '8px 10px', borderRadius: '6px',
                            background: '#fef3c7', border: '1px solid #fde68a',
                            color: '#92400e', fontSize: '12px',
                          }}>
                            <div>
                              <strong>{p.existing_manual}</strong> manual time {p.existing_manual === 1 ? 'entry' : 'entries'} already exist in this window (across all projects). Total daily hours may exceed contracted hours unless replaced.
                            </div>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '8px', cursor: 'pointer', color: '#92400e' }}>
                              <input
                                type="checkbox"
                                checked={backfillConfirm.replaceManual === true}
                                onChange={e => setBackfillConfirm(cur => cur ? { ...cur, replaceManual: e.target.checked } : cur)}
                              />
                              Replace existing manual entries in this window (any project)
                            </label>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
                <div style={{ display: 'flex', gap: '8px', marginTop: '16px', justifyContent: 'flex-end' }}>
                  <button
                    onClick={cancelBackfill}
                    disabled={saving}
                    style={{
                      fontSize: '12px', padding: '6px 14px', borderRadius: '4px',
                      background: '#fff', border: '1px solid var(--brio-border, #d1d5db)',
                      color: 'var(--brio-text, #475569)', cursor: 'pointer',
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmBackfill}
                    disabled={saving}
                    style={{
                      fontSize: '12px', padding: '6px 14px', borderRadius: '4px',
                      background: 'var(--brio-gold, #9F902A)', border: 'none', color: '#fff',
                      cursor: 'pointer', fontWeight: 600, opacity: saving ? 0.6 : 1,
                    }}
                  >
                    {saving
                      ? 'Working…'
                      : (backfillConfirm.mode === 'cleanup' ? 'Stop & delete entries' : 'Save & backfill')}
                  </button>
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export default function TeamMembersPage({
  // Data
  activeUsers,
  activeProjects,
  activeTeams,
  brioColors,
  hfbAllocations,
  // Current user (authoritative from Supabase auth mapping)
  currentUser,
  // Filters
  filters,
  setFilters,
  // State
  modal,
  form,
  setModal,
  setForm,
  // Formatters
  fmtCurr,
  // Supabase hooks for refetching
  supabaseUsers,
  // Persistence
  useSupabaseDB,
  users,
  setUsers,
  // Functions needed by edit modal
  getSalaryFromMatrix,
  jobRoles,
  calculateSalaryMarkupFactor,
  rateHistory,
  setRateHistory,
  // Team membership updates (for Add User flow)
  teams,
  setTeams,
  // Time entries for analytics in fullscreen
  activeTimeEntries,
  // Tasks (for auto-allocated logging mode pickers)
  activeTasks,
  // NL salary matrix (for custom role labels)
  salaryMatrixHistoryNL,
  // Salary resolution (from App.js — used by fullscreen salary tab)
  getInternalCostRate,
}) {
  const tableProps = getTablePropsFromCode(PRESENTATION_CODE);
  const modalTableProps = getTablePropsFromCode(MODAL_TABLE_CODE);

  // Extract custom role labels from the latest NL salary matrix revision
  const nlCustomRoles = React.useMemo(() => {
    if (!salaryMatrixHistoryNL || salaryMatrixHistoryNL.length === 0) return [];
    const sorted = [...salaryMatrixHistoryNL].sort((a, b) =>
      (b.effectiveDate || '').localeCompare(a.effectiveDate || '')
    );
    const customCols = sorted[0]?.customColumns || [];
    return customCols.map((cc, i) => cc.name || `Custom ${i + 1}`);
  }, [salaryMatrixHistoryNL]);

  // Map custom role display names → schaal IDs (e.g. "Director" → "c_1")
  const nlCustomRoleSchaalMap = React.useMemo(() => {
    if (!salaryMatrixHistoryNL || salaryMatrixHistoryNL.length === 0) return {};
    const sorted = [...salaryMatrixHistoryNL].sort((a, b) =>
      (b.effectiveDate || '').localeCompare(a.effectiveDate || '')
    );
    const customCols = sorted[0]?.customColumns || [];
    const map = {};
    customCols.forEach((cc, i) => {
      const name = cc.name || `Custom ${i + 1}`;
      map[name] = cc.id; // e.g. "c_1"
    });
    return map;
  }, [salaryMatrixHistoryNL]);

  // Extract custom tredes from the latest NL salary matrix revision.
  // Falls back to detecting extra trede keys from the matrix itself
  // (keys beyond "0"–"9" in any schaal row).
  const nlCustomTredes = React.useMemo(() => {
    if (!salaryMatrixHistoryNL || salaryMatrixHistoryNL.length === 0) return [];
    // Collect custom tredes from ALL revisions so tredes added to any
    // revision (current or future) are always available in the dropdown.
    const customMap = new Map(); // id -> { id, name }
    const standardTredes = new Set(['0','1','2','3','4','5','6','7','8','9']);
    for (const rev of salaryMatrixHistoryNL) {
      // Explicit customTredes array
      if (rev.customTredes && rev.customTredes.length > 0) {
        for (const ct of rev.customTredes) {
          if (!customMap.has(String(ct.id))) customMap.set(String(ct.id), ct);
        }
      }
      // Scan matrix keys for non-standard tredes
      if (rev.matrix) {
        Object.values(rev.matrix).forEach(schaalRow => {
          if (schaalRow && typeof schaalRow === 'object') {
            Object.keys(schaalRow).forEach(k => {
              if (!standardTredes.has(k) && !customMap.has(k)) {
                customMap.set(k, { id: k, name: `Trede ${k}` });
              }
            });
          }
        });
      }
    }
    return Array.from(customMap.values()).sort((a, b) => parseInt(a.id) - parseInt(b.id));
  }, [salaryMatrixHistoryNL]);

  // ========================================================================
  // FULLSCREEN OVERLAY STATE
  // ========================================================================
  const [isTeamMemberFullscreenOpen, setIsTeamMemberFullscreenOpen] = useState(false);
  const [selectedFullscreenUserId, setSelectedFullscreenUserId] = useState(null);

  const openFullscreen = (user) => {
    setSelectedFullscreenUserId(user.id);
    setIsTeamMemberFullscreenOpen(true);
  };

  const closeFullscreen = () => {
    setIsTeamMemberFullscreenOpen(false);
    setSelectedFullscreenUserId(null);
  };

  // ========================================================================
  // SUPERVISOR TEAM MEMBER IDs (for evaluation report gating)
  // Mirrors the weekly_team_assignments lookup used by useTimesheetOverview.
  // users.team_id is NULL for most members, so we use the HFB assignments table
  // as the authoritative source, with users.teamId as a fallback seed.
  // ========================================================================
  const [supervisorMemberIds, setSupervisorMemberIds] = useState(new Set());

  useEffect(() => {
    if (currentUser?.role !== 'supervisor') {
      setSupervisorMemberIds(new Set());
      return;
    }
    const supervisedTeamIdSet = new Set(
      (activeTeams || [])
        .filter(t => normalizedIncludes(t.supervisorIds || [], currentUser.id))
        .map(t => normalizeId(t.id))
    );
    if (supervisedTeamIdSet.size === 0) {
      setSupervisorMemberIds(new Set());
      return;
    }
    // Seed from users.teamId (fallback for members with team_id populated)
    const ids = new Set();
    for (const u of (activeUsers || [])) {
      if (u.teamId != null && supervisedTeamIdSet.has(normalizeId(u.teamId))) {
        ids.add(normalizeId(u.id));
      }
    }
    setSupervisorMemberIds(new Set(ids));
    // Augment with weekly_team_assignments (the primary/authoritative source)
    (async () => {
      try {
        const { data } = await supabase
          .from('weekly_team_assignments')
          .select('user_id')
          .in('team_id', Array.from(supervisedTeamIdSet));
        if (data) {
          const augmented = new Set(ids);
          data.forEach(a => augmented.add(normalizeId(a.user_id)));
          setSupervisorMemberIds(augmented);
        }
      } catch (err) {
        console.warn('[TeamMembersPage] Could not fetch HFB team assignments:', err);
      }
    })();
  }, [currentUser?.id, currentUser?.role, activeTeams, activeUsers]);

  // ========================================================================
  // LINK LOGIN MODAL STATE
  // ========================================================================
  const [showLinkLoginModal, setShowLinkLoginModal] = useState(false);
  const [linkLoginForm, setLinkLoginForm] = useState({
    selectedEmployeeId: '',
    authUserId: '',
    confirmRelink: false,
  });
  const [linkLoginError, setLinkLoginError] = useState(null);
  const [linkLoginLoading, setLinkLoginLoading] = useState(false);
  const [linkLoginSuccess, setLinkLoginSuccess] = useState(null);

  // ========================================================================
  // CREATE USER MODAL STATE
  // ========================================================================
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [createUserForm, setCreateUserForm] = useState({
    name: '',
    email: '',
    role: 'user',
    teamId: '',
    tempPassword: '',
  });
  const [createUserError, setCreateUserError] = useState(null);
  const [createUserLoading, setCreateUserLoading] = useState(false);
  const [createUserSuccess, setCreateUserSuccess] = useState(null);

  // ========================================================================
  // RESET PASSWORD MODAL STATE
  // ========================================================================
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [resetPasswordForm, setResetPasswordForm] = useState({
    userId: null,
    userName: '',
    userEmail: '',
    userStatus: '',
  });
  const [resetPasswordError, setResetPasswordError] = useState(null);
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);
  const [resetPasswordSuccess, setResetPasswordSuccess] = useState(null);

  // ========================================================================
  // LOGIN EMAIL UPDATE STATE (within Edit Team Member modal)
  // ========================================================================
  const [loginEmailValue, setLoginEmailValue] = useState('');
  const [loginEmailLoading, setLoginEmailLoading] = useState(false);
  const [loginEmailSuccess, setLoginEmailSuccess] = useState(null);
  const [loginEmailError, setLoginEmailError] = useState(null);

  // Ledger-derived annual bonus per year for the user being edited.
  // { [year]: annualTotal } — populated when the edit modal opens.
  const [editModalLedgerBonus, setEditModalLedgerBonus] = useState({});

  // Check if UUID is valid format
  const isValidUUID = (uuid) => {
    return UUID_REGEX.test(uuid?.trim() || '');
  };

  // Get selected employee data
  const selectedEmployee = activeUsers.find(u => String(u.id) === String(linkLoginForm.selectedEmployeeId));
  const selectedEmployeeAuthId = getAuthUserId(selectedEmployee);
  const employeeAlreadyLinked = selectedEmployeeAuthId && selectedEmployeeAuthId.trim() !== '';
  
  const getExistingLinkForUUID = (uuid) => {
    if (!uuid || !isValidUUID(uuid)) return null;
    const trimmedUUID = uuid.trim().toLowerCase();
    return activeUsers.find(u => {
      const userAuthId = getAuthUserId(u);
      return userAuthId && 
        userAuthId.trim().toLowerCase() === trimmedUUID &&
        String(u.id) !== String(linkLoginForm.selectedEmployeeId);
    });
  };
  
  const uuidAlreadyLinkedTo = getExistingLinkForUUID(linkLoginForm.authUserId);
  
  // RBAC
  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'admin-pa';
  const isSupervisor = currentUser?.role === 'supervisor';
  
  // Link login save guard
  const canSave = 
    isAdmin &&
    linkLoginForm.selectedEmployeeId &&
    isValidUUID(linkLoginForm.authUserId) &&
    !uuidAlreadyLinkedTo &&
    (!employeeAlreadyLinked || linkLoginForm.confirmRelink);

  // ========================================================================
  // LINK LOGIN HANDLERS
  // ========================================================================
  const openLinkLoginModal = () => {
    setLinkLoginForm({ selectedEmployeeId: '', authUserId: '', confirmRelink: false });
    setLinkLoginError(null);
    setLinkLoginSuccess(null);
    setShowLinkLoginModal(true);
  };

  const handleSaveLinkLogin = async () => {
    if (!isAdmin) {
      setLinkLoginError('Permission denied. Only administrators can link logins.');
      return;
    }
    if (!canSave) {
      setLinkLoginError('Please complete all required fields correctly.');
      return;
    }
    const trimmedUUID = linkLoginForm.authUserId.trim();
    const employeeId = parseInt(linkLoginForm.selectedEmployeeId, 10);
    if (isNaN(employeeId) || employeeId <= 0) {
      setLinkLoginError('Invalid employee selection.');
      return;
    }
    if (!isValidUUID(trimmedUUID)) {
      setLinkLoginError('Invalid UUID format.');
      return;
    }
    setLinkLoginLoading(true);
    setLinkLoginError(null);
    try {
      const { data, error } = await supabase
        .from('users')
        .update({ auth_user_id: trimmedUUID })
        .eq('id', employeeId)
        .select('id, auth_user_id');
      if (error) throw error;
      if (!data || data.length === 0) {
        throw new Error('Update failed. This may be due to insufficient permissions (RLS) or the employee record no longer exists.');
      }
      if (data[0].auth_user_id !== trimmedUUID) {
        throw new Error('Update was not applied. Please check your permissions.');
      }
      setLinkLoginSuccess(`Successfully linked login to ${selectedEmployee?.name}`);
      try {
        if (supabaseUsers?.refetch) await supabaseUsers.refetch();
        else if (supabaseUsers?.fetchUsers) await supabaseUsers.fetchUsers();
        else if (supabaseUsers?.reload) await supabaseUsers.reload();
      } catch (refetchErr) {
        console.warn('[LinkLogin] Refetch warning (non-fatal):', refetchErr);
      }
      setTimeout(() => {
        setShowLinkLoginModal(false);
        setLinkLoginSuccess(null);
      }, 1500);
    } catch (err) {
      console.error('[LinkLogin] Error linking auth user:', err);
      setLinkLoginError(err.message || 'Failed to link login. Please try again.');
    } finally {
      setLinkLoginLoading(false);
    }
  };

  // ========================================================================
  // CREATE USER HANDLERS
  // ========================================================================
  const openCreateUserModal = () => {
    setCreateUserForm({ name: '', email: '', role: 'user', teamId: '', tempPassword: '' });
    setCreateUserError(null);
    setCreateUserSuccess(null);
    setShowCreateUserModal(true);
  };

  const handleCreateUser = async () => {
    if (!isAdmin) { setCreateUserError('Permission denied. Only administrators can create users.'); return; }
    if (!createUserForm.name.trim()) { setCreateUserError('Name is required'); return; }
    if (!createUserForm.email.trim() || !createUserForm.email.includes('@')) { setCreateUserError('Valid email is required'); return; }
    if (!createUserForm.tempPassword || createUserForm.tempPassword.length < 6) { setCreateUserError('Password must be at least 6 characters'); return; }
    setCreateUserLoading(true);
    setCreateUserError(null);
    try {
      const result = await adminCreateUser({
        name: createUserForm.name.trim(),
        email: createUserForm.email.trim().toLowerCase(),
        role: createUserForm.role,
        team_id: createUserForm.teamId || null,
        tempPassword: createUserForm.tempPassword,
      });
      if (!result.success) {
        const details = result.errorDetails || {};
        let errorMessage;
        if (details.edgeMessage === 'NO_SESSION') errorMessage = 'Not signed in. Please sign in again.';
        else if (details.status === 403) errorMessage = 'Forbidden: Admin access required.';
        else if (details.status === 401 && details.edgeMessage?.toLowerCase().includes('invalid jwt')) errorMessage = 'Unauthorized (Invalid JWT). Please sign in again.';
        else if (details.status === 409) errorMessage = 'User with this email already exists.';
        else errorMessage = result.error || 'Failed to create user';
        throw new Error(errorMessage);
      }
      setCreateUserSuccess(`Successfully created/invited ${createUserForm.email}`);
      try { if (supabaseUsers?.refetch) await supabaseUsers.refetch(); } catch (refetchErr) { console.warn('[CreateUser] Refetch warning:', refetchErr); }
      setTimeout(() => { setShowCreateUserModal(false); setCreateUserSuccess(null); }, 1500);
    } catch (err) {
      console.error('[CreateUser] Error:', err);
      setCreateUserError(err.message || 'Failed to create user');
    } finally {
      setCreateUserLoading(false);
    }
  };

  // ========================================================================
  // RESET PASSWORD HANDLERS
  // ========================================================================
  const openResetPasswordModal = (user) => {
    setResetPasswordForm({ userId: user.id, userName: user.name, userEmail: user.email, userStatus: user.status });
    setResetPasswordError(null);
    setResetPasswordSuccess(null);
    setShowResetPasswordModal(true);
  };

  const handleResetPassword = async () => {
    if (!isAdmin) { setResetPasswordError('Permission denied.'); return; }
    setResetPasswordLoading(true);
    setResetPasswordError(null);
    try {
      const mode = resetPasswordForm.userStatus === 'invited' ? 'invite' : 'recovery';
      const result = await adminResetPassword({ email: resetPasswordForm.userEmail, mode });
      if (!result.success) {
        const details = result.errorDetails || {};
        let errorMessage;
        if (details.edgeMessage === 'NO_SESSION') errorMessage = 'Not signed in. Please sign in again.';
        else if (details.status === 403) errorMessage = 'Forbidden: Admin access required.';
        else if (details.status === 401 && details.edgeMessage?.toLowerCase().includes('invalid jwt')) errorMessage = 'Unauthorized (Invalid JWT). Please sign in again.';
        else errorMessage = result.error || 'Failed to send password reset email';
        throw new Error(errorMessage);
      }
      const successMsg = resetPasswordForm.userStatus === 'invited'
        ? `Setup email resent to ${resetPasswordForm.userName}`
        : `Password reset email sent to ${resetPasswordForm.userName}`;
      setResetPasswordSuccess(successMsg);
      setTimeout(() => { setShowResetPasswordModal(false); setResetPasswordSuccess(null); }, 1500);
    } catch (err) {
      console.error('[ResetPassword] Error:', err);
      setResetPasswordError(err.message || 'Failed to send password reset email');
    } finally {
      setResetPasswordLoading(false);
    }
  };

  // ========================================================================
  // UPDATE LOGIN EMAIL HANDLER (via Edge Function)
  // ========================================================================
  const handleUpdateLoginEmail = async () => {
    if (!isAdmin) { setLoginEmailError('Permission denied. Only administrators can change login emails.'); return; }

    const editedUser = activeUsers.find(u => u.id === form.editUserId);
    const authId = editedUser?.authUserId || editedUser?.auth_user_id;
    if (!authId) { setLoginEmailError('This user has no login linked. Use "Link Login to Employee" first.'); return; }

    const trimmedEmail = loginEmailValue.trim().toLowerCase();
    if (!trimmedEmail || !trimmedEmail.includes('@')) { setLoginEmailError('Please enter a valid email address.'); return; }

    setLoginEmailLoading(true);
    setLoginEmailError(null);
    setLoginEmailSuccess(null);

    try {
      // Call Edge Function directly via Supabase client (auth header sent automatically)
      const { data, error } = await supabase.functions.invoke('admin-update-email', {
        body: { userId: authId, newEmail: trimmedEmail },
      });

      // Handle invoke-level error (network, auth, non-2xx)
      if (error) {
        throw new Error(error.message || 'Edge Function call failed');
      }

      // Handle application-level error from the Edge Function response
      if (!data?.success) {
        throw new Error(data?.error || 'Edge Function returned an unexpected response');
      }

      // ── Success path ──
      setLoginEmailSuccess(`Login email updated to ${trimmedEmail}`);

      // Sync the profile email field in the open form so the user sees it immediately
      setForm(prev => ({ ...prev, userEmail: trimmedEmail }));

      // Refresh the team members list (non-fatal if it fails)
      try {
        if (supabaseUsers?.refetch) await supabaseUsers.refetch();
        else if (supabaseUsers?.fetchUsers) await supabaseUsers.fetchUsers();
      } catch (refetchErr) {
        console.warn('[UpdateLoginEmail] Refetch warning (non-fatal):', refetchErr);
      }

      // Clear success banner after a few seconds
      setTimeout(() => setLoginEmailSuccess(null), 3000);
    } catch (err) {
      console.error('[UpdateLoginEmail] Error:', err);
      setLoginEmailError(err.message || 'Failed to update login email');
    } finally {
      setLoginEmailLoading(false);
    }
  };

  // ========================================================================
  // EDIT USER SAVE HANDLER (moved from App.js — logic identical)
  // ========================================================================
  const handleSaveEditUser = async () => {
    if (!form.userName || !form.userEmail) {
      alert('Fill all required fields');
      return;
    }

    const originalUser = activeUsers.find(u => u.id === form.editUserId);

    // Track cost-affecting field changes for profile history
    // NOTE: jobRole/jobLevel changes are now handled exclusively via Role History
    const costAffectingChanges = {};
    const effectiveDate = form.profileEffectiveDate || new Date().toISOString().split('T')[0];

    if (originalUser) {
      const bonusYear = form.bonusYear || new Date().getFullYear();
      const originalBonuses = originalUser.bonuses?.[bonusYear] || { q1: 0, q2: 0, q3: 0, q4: 0 };
      const newBonuses = {
        q1: parseFloat(form.bonusQ1) || 0, q2: parseFloat(form.bonusQ2) || 0,
        q3: parseFloat(form.bonusQ3) || 0, q4: parseFloat(form.bonusQ4) || 0
      };
      const bonusChanged = originalBonuses.q1 !== newBonuses.q1 || originalBonuses.q2 !== newBonuses.q2 || originalBonuses.q3 !== newBonuses.q3 || originalBonuses.q4 !== newBonuses.q4;
      if (bonusChanged) {
        const oldTotal = originalBonuses.q1 + originalBonuses.q2 + originalBonuses.q3 + originalBonuses.q4;
        const newTotal = newBonuses.q1 + newBonuses.q2 + newBonuses.q3 + newBonuses.q4;
        costAffectingChanges[`bonuses_${bonusYear}`] = {
          from: `₹${oldTotal.toLocaleString()} (Q1:${originalBonuses.q1} Q2:${originalBonuses.q2} Q3:${originalBonuses.q3} Q4:${originalBonuses.q4})`,
          to: `₹${newTotal.toLocaleString()} (Q1:${newBonuses.q1} Q2:${newBonuses.q2} Q3:${newBonuses.q3} Q4:${newBonuses.q4})`
        };
      }
    }

    const currentYear = new Date().getFullYear();
    const newSalaryHistory = originalUser?.salaryHistory || [];

    // Profile history entry
    let newProfileHistory = originalUser?.profileHistory || [];
    if (Object.keys(costAffectingChanges).length > 0) {
      const profileChangeEntry = {
        id: Date.now(),
        effectiveDate: effectiveDate,
        changedBy: currentUser.email,
        changeReason: form.profileChangeReason || 'Profile update',
        changes: costAffectingChanges
      };
      newProfileHistory = [...newProfileHistory, profileChangeEntry];
      newProfileHistory.sort((a, b) => a.effectiveDate.localeCompare(b.effectiveDate));
    }

    const bonusYear = form.bonusYear || currentYear;
    const updatedBonuses = {
      ...(originalUser?.bonuses || {}),
      [bonusYear]: {
        q1: parseFloat(form.bonusQ1) || 0, q2: parseFloat(form.bonusQ2) || 0,
        q3: parseFloat(form.bonusQ3) || 0, q4: parseFloat(form.bonusQ4) || 0
      }
    };

    // NOTE: jobRole/jobLevel are NOT included — they are managed exclusively
    // via user_role_history and synced to users/user_profiles by DB trigger
    const updatedUserData = {
      name: form.userName,
      email: form.userEmail,
      role: form.userRole,
      isActive: form.isActive !== false,
      teamId: form.teamId || null,
      joinDate: form.joinDate,
      officeLocation: form.officeLocation || 'NL',
      salaryHistory: newSalaryHistory,
      profileHistory: newProfileHistory,
      bonuses: updatedBonuses
    };

    // Temp page access is admin-only. Only include in the update payload when
    // the admin is the editor and the form actually carried these fields.
    if (isAdmin && form.tempAccessPages !== undefined) {
      const requestedPages = Array.isArray(form.tempAccessPages) ? form.tempAccessPages : [];
      const expiryIso = requestedPages.length === 0
        ? null
        : tempAccessDateToIso(form.tempAccessExpiresAt);
      // Block save when pages are selected without a future expiry — both the
      // frontend gate and the RLS function reject grants with a null/past
      // expiry, so silently persisting them would create a dead grant.
      if (requestedPages.length > 0 && (!expiryIso || new Date(expiryIso).getTime() <= Date.now())) {
        alert('Please set a future "Access Expires" date for the temporary page access grant, or uncheck all pages to revoke the grant.');
        return;
      }
      updatedUserData.tempAccessPages = requestedPages;
      updatedUserData.tempAccessExpiresAt = expiryIso;
    }

    if (useSupabaseDB) {
      const result = await supabaseUsers.updateUser(form.editUserId, updatedUserData);
      if (!result.success) { alert('Error updating user: ' + result.error); return; }
      // Save user_profiles fields (profile_intro)
      const profileUpsert = { user_id: form.editUserId };
      if (form.profileIntro !== undefined) profileUpsert.profile_intro = form.profileIntro || null;
      if (Object.keys(profileUpsert).length > 1) {
        // Use update-then-insert instead of upsert with onConflict to avoid
        // silent failures when user_profiles.user_id has no UNIQUE constraint.
        // Supabase query errors are returned as { error }, not thrown, so
        // try/catch alone does not protect against them.
        const { user_id: profileUserId, ...profileFields } = profileUpsert;
        const { data: updatedRows, error: updateErr } = await supabase
          .from('user_profiles')
          .update(profileFields)
          .eq('user_id', profileUserId)
          .select('user_id');

        if (updateErr) {
          console.warn('[EditUser] user_profiles update failed:', updateErr.message);
        } else if (!updatedRows || updatedRows.length === 0) {
          // No existing profile row — insert one
          const { error: insertErr } = await supabase
            .from('user_profiles')
            .insert({ ...profileFields, user_id: profileUserId });
          if (insertErr) console.warn('[EditUser] user_profiles insert failed:', insertErr.message);
        }

        // Re-fetch users so profile fields are reflected immediately
        if (supabaseUsers?.refetch) await supabaseUsers.refetch();
      }
    } else {
      setUsers(users.map(u => u.id === form.editUserId ? { ...u, ...updatedUserData } : u));
    }

    // Show profile change alert
    if (Object.keys(costAffectingChanges).length > 0) {
      const changes = Object.keys(costAffectingChanges).map(field => {
        const change = costAffectingChanges[field];
        return `${field}: ${change.from} → ${change.to}`;
      }).join('\n');
      alert(`✓ User updated!\n\nProfile changes effective from ${effectiveDate}:\n${changes}\n\nCost calculations will use the new profile from this date onwards.`);
    }

    setModal(null);
    setForm({});
  };

  // ========================================================================
  // ADD TEAM MEMBER HANDLER (moved from App.js — logic identical)
  // ========================================================================
  const handleAddUser = async () => {
    const missing = [];
    if (!form.userName) missing.push('Name');
    if (!form.userEmail) missing.push('Email');
    if (!form.joinDate) missing.push('Join Date');
    if (form.markup === undefined || form.markup === null || form.markup === '') missing.push('Job Role & Level (needed for markup rate)');
    if (missing.length > 0) {
      alert(`Please fill all required fields: ${missing.join(', ')}`);
      return;
    }
    const newUser = {
      id: Date.now(),
      name: form.userName,
      email: form.userEmail,
      role: form.userRole || 'user',
      isActive: true,
      jobRole: form.jobRole || null,
      jobLevel: form.jobLevel !== undefined ? form.jobLevel : null,
      teamId: form.userTeam || null,
      markup: parseFloat(form.markup),
      joinDate: form.joinDate,
      officeLocation: form.officeLocation || 'NL',
    };

    let createdUserId = newUser.id;
    if (useSupabaseDB) {
      const result = await supabaseUsers.createUser(newUser);
      if (!result.success) {
        alert('Error creating user: ' + result.error);
        return;
      }
      // Use DB-generated ID for role history
      createdUserId = result.data?.id || newUser.id;
    } else {
      setUsers([...users, newUser]);
    }

    // Update team memberIds if team selected
    if (form.userTeam && setTeams && teams) {
      setTeams(teams.map(t => t.id === form.userTeam ? {
        ...t,
        memberIds: [...(t.memberIds || []), createdUserId]
      } : t));
    }

    // Create initial role_history row (authoritative source for role/level)
    if (useSupabaseDB && form.jobRole && form.jobLevel !== undefined) {
      try {
        const { data: rpcResult, error: rpcError } = await supabase.rpc('role_history_add_row', {
          p_user_id: createdUserId,
          p_effective_from: form.joinDate,
          p_effective_to: null,
          p_job_role: form.jobRole,
          p_job_level: parseInt(form.jobLevel) || 0,
          p_office_location: form.officeLocation || 'NL',
          p_source: 'initial',
          p_confidence: 'high',
          p_notes: 'Created with new team member',
        });
        if (rpcError) console.warn('[AddUser] Could not create initial role history:', rpcError);
        else if (rpcResult && !rpcResult.success) console.warn('[AddUser] Role history RPC error:', rpcResult.error);
      } catch (err) {
        console.warn('[AddUser] Could not create initial role history:', err);
      }
    }

    // Create rate history entry for this user
    if (form.jobRole && form.jobLevel !== undefined) {
      const now = new Date();
      const effectiveMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
      setRateHistory([...rateHistory, {
        id: Date.now() + 1,
        userId: createdUserId,
        markup: parseFloat(form.markup),
        effectiveMonth: effectiveMonth,
      }]);
    }

    setModal(null);
    setForm({});
  };
  const getAllocationColor = (rawPercent) => {
    if (rawPercent > 100) return '#ef4444';
    if (rawPercent === 100) return '#22c55e';
    return '#9ca3af';
  };

  const getInitials = (name) => {
    if (!name) return '??';
    return name.split(' ').map(n => n[0]).join('');
  };

  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });

  const handleSortClick = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };
  const getSortIndicator = (key) => {
    if (sortConfig.key !== key) return '';
    return sortConfig.direction === 'asc' ? ' ↑' : ' ↓';
  };

  const effectiveEmploymentStatus = filters.employmentStatus ?? 'active';

  const filteredUsers = activeUsers
    .filter(u => {
      if (effectiveEmploymentStatus === 'active') return u.isActive !== false;
      if (effectiveEmploymentStatus === 'inactive') return u.isActive === false;
      return true;
    })
    .sort((a, b) => {
      let aVal, bVal;
      switch (sortConfig.key) {
        case 'role':   aVal = a.role || ''; bVal = b.role || ''; break;
        case 'jobRole': aVal = a.effectiveJobRole ?? a.jobRole ?? ''; bVal = b.effectiveJobRole ?? b.jobRole ?? ''; break;
        case 'joinDate': aVal = a.joinDate || ''; bVal = b.joinDate || ''; break;
        default:       aVal = a.name || ''; bVal = b.name || ''; break;
      }
      const cmp = String(aVal).localeCompare(String(bVal));
      return sortConfig.direction === 'asc' ? cmp : -cmp;
    });

  // ── Cost-change detection for the edit modal ──────────────────────
  const getEditCostChanges = () => {
    const originalUser = activeUsers.find(u => u.id === form.editUserId);
    if (!originalUser) return null;
    const bonusYear = form.bonusYear || new Date().getFullYear();
    const originalBonuses = originalUser.bonuses?.[bonusYear] || { q1: 0, q2: 0, q3: 0, q4: 0 };
    const newBonuses = {
      q1: parseFloat(form.bonusQ1) || 0, q2: parseFloat(form.bonusQ2) || 0,
      q3: parseFloat(form.bonusQ3) || 0, q4: parseFloat(form.bonusQ4) || 0
    };
    const bonusChanged = originalBonuses.q1 !== newBonuses.q1 || originalBonuses.q2 !== newBonuses.q2 || originalBonuses.q3 !== newBonuses.q3 || originalBonuses.q4 !== newBonuses.q4;
    // NOTE: jobRole/jobLevel changes are tracked via Role History, not here
    const hasChanges = bonusChanged;
    if (!hasChanges) return null;
    return { originalUser, bonusYear, originalBonuses, newBonuses, bonusChanged };
  };

  // Derived at render time.  Determines whether the currently-selected
  // fullscreen member belongs to one of the supervisor's teams.
  // Uses supervisorMemberIds (built from weekly_team_assignments) which is
  // the same authoritative source used by TimesheetOverview.
  const fsSelectedMember = selectedFullscreenUserId
    ? activeUsers.find(u => normalizedIncludes([u.id], selectedFullscreenUserId))
    : null;
  const fsSupervisorIsInMembersTeam = isSupervisor && fsSelectedMember != null
    ? supervisorMemberIds.has(normalizeId(fsSelectedMember.id))
    : false;

  return (
    <div className="brio-flex brio-flex-col brio-analytics-font">
      {/* PAGE HEADER BAND */}
      <div className="brio-pageband brio-pageband--header">
        <BrioFrame>
          <div className="brio-page-header__content">
            <div className="brio-page-header__left">

              <p className="brio-analytics-muted brio-mt-xs">Manage team members, roles, and project assignments</p>
            </div>
            <div className="brio-page-header__right">
              <button
                onClick={() => setFilters({...filters, employmentStatus: effectiveEmploymentStatus === 'active' ? 'inactive' : 'active'})}
                className={`brio-btn ${effectiveEmploymentStatus === 'active' ? 'brio-btn-primary' : 'brio-btn-secondary'}`}
              >
                {effectiveEmploymentStatus === 'active' ? 'Active' : 'Inactive'}
              </button>
              {isAdmin && (
                <button onClick={openLinkLoginModal} className="brio-btn brio-btn-secondary" title="Link a Supabase Auth account to an employee">
                  <Link className="w-4 h-4"/>Link Login
                </button>
              )}
              {isAdmin && (
                <button onClick={openCreateUserModal} className="brio-btn brio-btn-secondary" title="Create a new user with login credentials">
                  <UserPlus className="w-4 h-4"/>Invite User
                </button>
              )}
              <button onClick={() => setModal('addUser')} className="brio-btn brio-btn-primary">
                <Plus className="w-4 h-4"/>Add Team Member
              </button>
            </div>
          </div>
        </BrioFrame>
      </div>

      {/* CONTENT AREA */}
      <BrioFrame>
        <div className="brio-gap-s" style={{ paddingTop: 'var(--brio-space-s)' }}>
          {filteredUsers.length > 0 ? (
            <BrioAnalyticsSurface padding="m">
              <BrioByCode code={PRESENTATION_CODE}>
                <BrioTable {...tableProps} style={{ tableLayout: 'fixed', width: '100%' }}>
                  <colgroup>
                    <col style={{ width: isAdmin ? '32%' : '38%' }} />
                    <col style={{ width: isAdmin ? '34%' : '40%' }} />
                    <col style={{ width: '12%' }} />
                    {isAdmin && <col style={{ width: '10%' }} />}
                    <col style={{ width: '12%' }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th style={{ cursor: 'pointer' }} onClick={() => handleSortClick('name')}>Member{getSortIndicator('name')}</th>
                      <th style={{ cursor: 'pointer' }} onClick={() => handleSortClick('jobRole')}>Role & Team{getSortIndicator('jobRole')}</th>
                      <th className="text-center" style={{ whiteSpace: 'nowrap', cursor: 'pointer' }} onClick={() => handleSortClick('joinDate')}>Joined{getSortIndicator('joinDate')}</th>
                      {isAdmin && <th className="text-center" style={{ whiteSpace: 'nowrap' }}>Login</th>}
                      <th className="text-center" style={{ whiteSpace: 'nowrap' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map(u => {
                      const supervisedProjects = u.role === 'supervisor' 
                        ? activeProjects.filter(p => p.supervisors?.includes(u.id))
                        : [];
                      const userTeam = activeTeams.find(t => t.id === u.teamId);
                      const roleFill = brioColors.roles[u.jobRole] || '#D4C4A0';
                      const userHfbAllocations = (hfbAllocations || []).filter(a => String(a.userId) === String(u.id));
                      const rawAllocationPercent = userHfbAllocations.reduce((sum, a) => sum + (a.percentage || 0), 0);
                      const allocationPercent = Math.min(100, Math.max(0, rawAllocationPercent));
                      const userAuthId = getAuthUserId(u);
                      const hasAuthLinked = userAuthId && userAuthId.trim() !== '';
                      
                      return (
                        <tr key={u.id} className="brio-row--hoverable">
                          <td style={{ overflow: 'hidden' }}>
                            <div className="brio-flex brio-items-center brio-gap-xs" style={{ minWidth: 0 }}>
                              <BrioUserIcon
                                initials={getInitials(u.name)}
                                ringColor={getAllocationColor(rawAllocationPercent)}
                                fillColor={roleFill}
                                size={36}
                                ringWidth={3}
                                progress={allocationPercent}
                                title={`${(u.effectiveJobRole ?? u.jobRole) || 'No role'} - ${userTeam?.name || 'No team'} | HFB: ${rawAllocationPercent}%`}
                              />
                              <div style={{ minWidth: 0, flex: 1 }}>
                                <div className="brio-font-bold" style={{ fontSize: '11px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.name}</div>
                                <div className="brio-text-xs brio-text-grey" style={{ fontSize: '10px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.email}</div>
                                {u.role === 'supervisor' && supervisedProjects.length > 0 && (
                                  <div style={{ fontSize: '10px', color: 'var(--brio-gold)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    Supervises: {supervisedProjects.map(p => p.projectCode || '').filter(Boolean).join(', ')}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td>
                            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                              {(u.effectiveJobRole || u.jobRole) && (
                                <span style={{
                                  background: roleFill || '#D4C4A0',
                                  color: '#fff',
                                  display: 'inline-block',
                                  minWidth: 155,
                                  textAlign: 'center',
                                  whiteSpace: 'nowrap',
                                  padding: '2px 8px',
                                  fontSize: 'var(--brio-font-size-s, 11px)',
                                  fontFamily: 'Raleway, sans-serif',
                                  fontWeight: 500,
                                  borderRadius: 2,
                                }}>
                                  {u.effectiveJobRole ?? u.jobRole} - {(() => {
                                    const lvl = u.effectiveJobLevel ?? u.jobLevel;
                                    const ct = nlCustomTredes.find(t => String(t.id) === String(lvl));
                                    return ct ? ct.name : `L${lvl}`;
                                  })()}
                                </span>
                              )}
                              <span className={`brio-badge ${(u.role === 'admin' || u.role === 'admin-pa') ? 'brio-badge--warning' : u.role === 'supervisor' ? 'brio-badge--pending' : 'brio-badge--approved'}`}>
                                {u.role}
                              </span>
                              {u.isActive === false && <span className="brio-badge brio-badge--danger">✗ Inactive</span>}
                            </div>
                          </td>
                          <td className="text-center" style={{ whiteSpace: 'nowrap' }}>
                            <span className="brio-text-grey">{u.joinDate || 'N/A'}</span>
                          </td>
                          {isAdmin && (
                            <td className="text-center" style={{ whiteSpace: 'nowrap' }}>
                              {hasAuthLinked ? (
                                <span className="brio-badge brio-badge--approved" title={`Auth ID: ${userAuthId}`} style={{ cursor: 'help' }}>✓ Linked</span>
                              ) : (
                                <span
                                  className="brio-badge"
                                  style={{ backgroundColor: '#fef3c7', color: '#92400e', cursor: 'pointer' }}
                                  title="Click to link login"
                                  onClick={() => {
                                    setLinkLoginForm({ selectedEmployeeId: String(u.id), authUserId: '', confirmRelink: false });
                                    setLinkLoginError(null);
                                    setLinkLoginSuccess(null);
                                    setShowLinkLoginModal(true);
                                  }}
                                >⚠ No login</span>
                              )}
                            </td>
                          )}
                          <td className="text-center" style={{ width: '1%', whiteSpace: 'nowrap' }}>
                            <BrioActionGroup center>
                              {isSupervisor ? (
                                <span style={{ opacity: 0.4, cursor: 'not-allowed', display: 'inline-block' }} title="Editing restricted (admin only)">
                                  <BrioIconButton icon="edit" variant="edit" title="Editing restricted (admin only)" onClick={(e) => { e.preventDefault(); e.stopPropagation(); }} />
                                </span>
                              ) : (
                                <BrioIconButton
                                  icon="edit"
                                  variant="edit"
                                  title="Edit team member"
                                  onClick={async (e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    const currentYear = new Date().getFullYear();
                                    const yearBonuses = u.bonuses?.[currentYear] || { q1: 0, q2: 0, q3: 0, q4: 0 };
                                    // Fetch profile_intro from user_profiles table
                                    let profileIntro = '';
                                    try {
                                      const { data: profileRow } = await supabase
                                        .from('user_profiles').select('profile_intro').eq('user_id', u.id).maybeSingle();
                                      if (profileRow) profileIntro = profileRow.profile_intro || '';
                                    } catch (err) { console.warn('[EditUser] Could not fetch profile_intro:', err); }
                                    // Fetch ledger bonus totals per year (fallback when users.bonuses is not set)
                                    let ledgerBonus = {};
                                    try {
                                      const authId = u.authUserId || u.auth_user_id;
                                      if (authId) {
                                        const { data: ledgerRows } = await supabase
                                          .from('salary_monthly_ledger')
                                          .select('month, bonus')
                                          .eq('auth_user_id', authId)
                                          .gt('bonus', 0);
                                        if (ledgerRows) {
                                          for (const row of ledgerRows) {
                                            const yr = parseInt(row.month.substring(0, 4), 10);
                                            ledgerBonus[yr] = (ledgerBonus[yr] || 0) + (parseFloat(row.bonus) || 0);
                                          }
                                        }
                                      }
                                    } catch (err) { console.warn('[EditUser] Could not fetch ledger bonus:', err); }
                                    setEditModalLedgerBonus(ledgerBonus);
                                    // If users.bonuses[currentYear] is all-zero, pre-populate from ledger (annual/4 per quarter)
                                    const totalManualBonus = (yearBonuses.q1 || 0) + (yearBonuses.q2 || 0) + (yearBonuses.q3 || 0) + (yearBonuses.q4 || 0);
                                    let initQ1 = yearBonuses.q1 || 0, initQ2 = yearBonuses.q2 || 0;
                                    let initQ3 = yearBonuses.q3 || 0, initQ4 = yearBonuses.q4 || 0;
                                    if (totalManualBonus === 0 && ledgerBonus[currentYear] > 0) {
                                      const perQ = Math.round(ledgerBonus[currentYear] / 4);
                                      initQ1 = initQ2 = initQ3 = initQ4 = perQ;
                                    }
                                    setForm({
                                      editUserId: u.id,
                                      userName: u.name,
                                      userEmail: u.email,
                                      userRole: u.role,
                                      jobRole: u.jobRole,
                                      jobLevel: u.jobLevel,
                                      markup: u.markup,
                                      teamId: u.teamId || null,
                                      joinDate: u.joinDate || '',
                                      isActive: u.isActive !== false,
                                      officeLocation: u.officeLocation || 'NL',
                                      bonusYear: currentYear,
                                      bonusQ1: initQ1,
                                      bonusQ2: initQ2,
                                      bonusQ3: initQ3,
                                      bonusQ4: initQ4,
                                      authUserId: u.authUserId || u.auth_user_id || null,
                                      userStatus: u.status || null,
                                      profileIntro,
                                      tempAccessPages: Array.isArray(u.tempAccessPages) ? [...u.tempAccessPages] : [],
                                      tempAccessExpiresAt: tempAccessIsoToDateInput(u.tempAccessExpiresAt),
                                    });
                                    setModal('editUser');
                                    // Reset login email state for fresh modal
                                    setLoginEmailValue('');
                                    setLoginEmailError(null);
                                    setLoginEmailSuccess(null);
                                  }}
                                />
                              )}
                              {/* Fullscreen detail page — visible to admins and supervisors */}
                              <button
                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); openFullscreen(u); }}
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: '28px',
                                  height: '28px',
                                  padding: 0,
                                  background: 'transparent',
                                  border: '1px solid var(--brio-border, #e2e8f0)',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  color: 'var(--brio-gold, #9F902A)',
                                  transition: 'background 0.15s',
                                }}
                                title="Open full detail page"
                              >
                                <Maximize2 style={{ width: 14, height: 14 }} />
                              </button>
                              {isAdmin && hasAuthLinked && (
                                <button
                                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); openResetPasswordModal(u); }}
                                  className="brio-btn brio-btn-secondary"
                                  style={{ padding: '4px 8px', fontSize: '12px' }}
                                  title={u.status === 'invited' ? 'Resend setup email' : 'Reset password'}
                                >
                                  <Key className="w-3 h-3" />
                                </button>
                              )}
                            </BrioActionGroup>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </BrioTable>
              </BrioByCode>
            </BrioAnalyticsSurface>
          ) : (
            <BrioAnalyticsSurface padding="m">
              <div className="brio-text-center brio-analytics-muted" style={{ padding: '48px 0' }}>
                No team members match the current filter
              </div>
            </BrioAnalyticsSurface>
          )}
        </div>
      </BrioFrame>

      {/* ════════════════════════════════════════════════════════════════
          ADD TEAM MEMBER MODAL — BRIO table 1b form layout
          (moved from App.js, restyled to match Edit modal)
         ════════════════════════════════════════════════════════════════ */}
      {modal === 'addUser' && (
        <BrioModal
          title="Add Team Member"
          onClose={() => { setModal(null); setForm({}); }}
          width="lg"
          footer={
            <BrioActionGroup>
              <button
                onClick={() => { setModal(null); setForm({}); }}
                className="brio-btn brio-btn-secondary"
              >
                Cancel
              </button>
              <button onClick={handleAddUser} className="brio-btn brio-btn-primary">
                Add User
              </button>
            </BrioActionGroup>
          }
        >
          <BrioByCode code={MODAL_TABLE_CODE}>
            <BrioTable {...modalTableProps}>
              <tbody>

                {/* ── Identity ──────────────────────────────────── */}
                <FormRow sectionHeader label="Identity" />

                <FormRow label="Name *">
                  <input
                    type="text"
                    placeholder="Full Name"
                    value={form.userName || ''}
                    onChange={e => setForm({ ...form, userName: e.target.value })}
                    style={INPUT_STYLE}
                  />
                </FormRow>

                <FormRow label="Email *">
                  <input
                    type="email"
                    placeholder="Email"
                    value={form.userEmail || ''}
                    onChange={e => setForm({ ...form, userEmail: e.target.value })}
                    style={INPUT_STYLE}
                  />
                </FormRow>

                <FormRow label="Join Date *" hint="Affects leave entitlement">
                  <BrioDatePicker
                    value={form.joinDate || ''}
                    onChange={v => setForm({ ...form, joinDate: v })}
                    style={{ maxWidth: '180px' }}
                  />
                </FormRow>

                <FormRow label="Office">
                  <select
                    value={form.officeLocation || 'NL'}
                    onChange={e => setForm({ ...form, officeLocation: e.target.value })}
                    style={{ ...SELECT_STYLE, maxWidth: '180px' }}
                  >
                    <option value="NL">NL</option>
                    <option value="IN">IN</option>
                  </select>
                </FormRow>

                {/* ── Role & Team ─────────────────────────────── */}
                <FormRow sectionHeader label="Role & Team" />

                <FormRow label="Permission Role">
                  <select
                    value={form.userRole || 'user'}
                    onChange={e => setForm({ ...form, userRole: e.target.value })}
                    style={SELECT_STYLE}
                  >
                    <option value="">Select Permission Role</option>
                    <option value="user">User – Can log time</option>
                    <option value="supervisor">Supervisor – Can manage assigned projects</option>
                    <option value="admin">Admin – Full access</option>
                  </select>
                  {form.userRole === 'supervisor' && (
                    <div style={HINT_STYLE}>Assign projects to supervisors in the Edit Project dialog</div>
                  )}
                </FormRow>

                <FormRow label="Team">
                  <select
                    value={form.userTeam || ''}
                    onChange={e => setForm({ ...form, userTeam: parseInt(e.target.value) || '' })}
                    style={SELECT_STYLE}
                  >
                    <option value="">Select Team</option>
                    {activeTeams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </FormRow>

                {/* ── Salary & Level ────────────────────────────── */}
                <FormRow sectionHeader label="Salary & Level" />

                <FormRow label="Job Role">
                  <select
                    value={form.jobRole || ''}
                    onChange={e => {
                      const newRole = e.target.value;
                      const level = form.jobLevel || 0;
                      const matrixData = getSalaryFromMatrix(newRole, level);
                      setForm({ ...form, jobRole: newRole, markup: matrixData.markup });
                    }}
                    style={SELECT_STYLE}
                  >
                    <option value="">Select Job Role</option>
                    {jobRoles.map(jr => <option key={jr} value={jr}>{jr}</option>)}
                  </select>
                </FormRow>

                <FormRow label={(form.officeLocation || 'NL') === 'NL' ? 'Trede' : 'Level'}>
                  <select
                    value={form.jobLevel !== undefined ? form.jobLevel : ''}
                    onChange={e => {
                      const newLevel = parseInt(e.target.value);
                      const role = form.jobRole;
                      const matrixData = getSalaryFromMatrix(role, newLevel);
                      setForm({ ...form, jobLevel: newLevel, markup: matrixData.markup });
                    }}
                    style={{ ...SELECT_STYLE, maxWidth: '160px' }}
                  >
                    {(form.officeLocation || 'NL') === 'NL' ? (
                      <>
                        <option value="">Select Trede (0-9)</option>
                        {Array.from({ length: 10 }, (_, i) => (
                          <option key={i} value={i}>Trede {i}</option>
                        ))}
                        {nlCustomTredes.map(ct => (
                          <option key={ct.id} value={ct.id}>{ct.name || `Trede ${ct.id}`}</option>
                        ))}
                      </>
                    ) : (
                      <>
                        <option value="">Select Level (0-20)</option>
                        {Array.from({ length: 21 }, (_, i) => (
                          <option key={i} value={i}>Level {i}</option>
                        ))}
                      </>
                    )}
                  </select>
                </FormRow>

              </tbody>
            </BrioTable>
          </BrioByCode>
        </BrioModal>
      )}

      {/* ════════════════════════════════════════════════════════════════
          EDIT TEAM MEMBER MODAL — BRIO table 1b form layout
         ════════════════════════════════════════════════════════════════ */}
      {modal === 'editUser' && (
        <BrioModal
          title="Edit Team Member"
          onClose={() => { setModal(null); setForm({}); }}
          width="lg"
          footer={
            <BrioActionGroup>
              <button onClick={() => setModal('confirmDeleteUser')} className="brio-btn brio-btn-danger">
                Delete User
              </button>
              {/* Admin: Reset Password / Resend Setup Email */}
              {isAdmin && (() => {
                const editedUser = activeUsers.find(u => u.id === form.editUserId);
                const hasAuth = editedUser?.authUserId || editedUser?.auth_user_id;
                const userStatus = editedUser?.status || 'active';
                const btnLabel = userStatus === 'invited' ? 'Resend setup email' : 'Reset password';
                return (
                  <button
                    onClick={async () => {
                      if (!editedUser?.email) { alert('No email found for this user'); return; }
                      if (!hasAuth) { alert('This user has no login linked. Use "Link Login to Employee" first.'); return; }
                      try {
                        const mode = userStatus === 'invited' ? 'invite' : 'recovery';
                        const result = await adminResetPassword({ email: editedUser.email, mode });
                        if (result.success) alert('✅ Email sent to ' + editedUser.email);
                        else alert('❌ Failed: ' + (result.error || 'Unknown error'));
                      } catch (err) { alert('❌ Failed: ' + (err.message || 'Unknown error')); }
                    }}
                    className="brio-btn brio-btn-secondary"
                  >
                    {btnLabel}
                  </button>
                );
              })()}
              <button
                onClick={() => { setModal(null); setForm({}); }}
                className="brio-btn brio-btn-secondary"
              >
                Cancel
              </button>
              <button onClick={handleSaveEditUser} className="brio-btn brio-btn-primary">
                Update User
              </button>
            </BrioActionGroup>
          }
        >
          <BrioByCode code={MODAL_TABLE_CODE}>
            <BrioTable {...modalTableProps}>
              <tbody>

                {/* ── Identity ──────────────────────────────────── */}
                <FormRow sectionHeader label="Identity" />

                <FormRow label="Name">
                  <input
                    type="text"
                    placeholder="Full Name"
                    value={form.userName || ''}
                    onChange={e => setForm({ ...form, userName: e.target.value })}
                    style={INPUT_STYLE}
                  />
                </FormRow>

                <FormRow label="Email">
                  <input
                    type="email"
                    placeholder="Email"
                    value={form.userEmail || ''}
                    onChange={e => setForm({ ...form, userEmail: e.target.value })}
                    style={INPUT_STYLE}
                  />
                </FormRow>

                {/* ── Login Email (admin-only, requires linked auth) ── */}
                {isAdmin && form.authUserId && (
                  <FormRow label="Login Email" hint="Auth email used to sign in">
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                      <input
                        type="email"
                        placeholder="new-login@email.com"
                        value={loginEmailValue}
                        onChange={e => { setLoginEmailValue(e.target.value); setLoginEmailError(null); setLoginEmailSuccess(null); }}
                        disabled={loginEmailLoading}
                        style={{ ...INPUT_STYLE, flex: 1, minWidth: '200px', opacity: loginEmailLoading ? 0.6 : 1 }}
                      />
                      <button
                        onClick={handleUpdateLoginEmail}
                        disabled={loginEmailLoading || !loginEmailValue.trim()}
                        className="brio-btn brio-btn-secondary"
                        style={{
                          fontSize: '12px',
                          padding: '6px 12px',
                          whiteSpace: 'nowrap',
                          opacity: (loginEmailLoading || !loginEmailValue.trim()) ? 0.5 : 1,
                        }}
                      >
                        {loginEmailLoading ? 'Updating…' : 'Update Login Email'}
                      </button>
                    </div>
                    {loginEmailSuccess && (
                      <div style={{ marginTop: '4px', fontSize: '12px', color: '#166534', background: '#dcfce7', border: '1px solid #86efac', borderRadius: '4px', padding: '4px 8px' }}>
                        ✓ {loginEmailSuccess}
                      </div>
                    )}
                    {loginEmailError && (
                      <div style={{ marginTop: '4px', fontSize: '12px', color: '#991b1b', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '4px', padding: '4px 8px' }}>
                        ✗ {loginEmailError}
                      </div>
                    )}
                  </FormRow>
                )}

                <FormRow label="Join Date" hint="Affects leave entitlement">
                  <BrioDatePicker
                    value={form.joinDate || ''}
                    onChange={v => setForm({ ...form, joinDate: v })}
                    style={{ maxWidth: '180px' }}
                  />
                </FormRow>

                <FormRow label="Office">
                  <select
                    value={form.officeLocation || 'NL'}
                    onChange={e => setForm({ ...form, officeLocation: e.target.value })}
                    style={{ ...SELECT_STYLE, maxWidth: '180px' }}
                  >
                    <option value="NL">NL</option>
                    <option value="IN">IN</option>
                  </select>
                  <div style={HINT_STYLE}>Primary office for cost allocation</div>
                </FormRow>

                <FormRow label="Profile Intro" hint="Short biography or introduction">
                  <textarea
                    value={form.profileIntro || ''}
                    onChange={e => setForm({ ...form, profileIntro: e.target.value })}
                    placeholder="A few words about this team member…"
                    rows={3}
                    style={{
                      ...INPUT_STYLE,
                      resize: 'none',
                      overflow: 'hidden',
                      minHeight: '60px',
                      lineHeight: '1.5',
                      fontFamily: "'Raleway', sans-serif",
                    }}
                    onInput={e => {
                      e.target.style.height = 'auto';
                      e.target.style.height = e.target.scrollHeight + 'px';
                    }}
                  />
                </FormRow>

                {/* ── Role & Status ─────────────────────────────── */}
                <FormRow sectionHeader label="Role & Status" />

                <FormRow label="Permission Role">
                  <select
                    value={form.userRole || 'user'}
                    onChange={e => setForm({ ...form, userRole: e.target.value })}
                    style={SELECT_STYLE}
                  >
                    <option value="">Select Permission Role</option>
                    <option value="user">User – Can log time</option>
                    <option value="supervisor">Supervisor – Can manage assigned projects</option>
                    <option value="admin">Admin – Full access</option>
                  </select>
                  {form.userRole === 'supervisor' && (
                    <div style={HINT_STYLE}>Assign projects to supervisors in the Edit Project dialog</div>
                  )}
                </FormRow>

                <FormRow label="Employment">
                  <div style={{ display: 'flex', gap: '16px', padding: '4px 0' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '13px' }}>
                      <input type="radio" name="isActive" checked={form.isActive !== false} onChange={() => setForm({ ...form, isActive: true })} style={{ width: '14px', height: '14px' }} />
                      <span style={{ fontWeight: 500, color: '#15803d' }}>✓ Active</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '13px' }}>
                      <input type="radio" name="isActive" checked={form.isActive === false} onChange={() => setForm({ ...form, isActive: false })} style={{ width: '14px', height: '14px' }} />
                      <span style={{ fontWeight: 500, color: 'var(--brio-muted, #94a3b8)' }}>✗ Inactive</span>
                    </label>
                  </div>
                  <div style={HINT_STYLE}>Inactive users can be filtered out from views and reports</div>
                </FormRow>

                {/* ── Temporary Page Access (admin-only) ────────── */}
                {isAdmin && (
                  <>
                    <FormRow sectionHeader label="Temporary Page Access" />
                    <FormRow label="Pages">
                      <div style={{ ...HINT_STYLE, marginTop: 0, marginBottom: '6px' }}>Grants this user access to selected pages until the expiry date below, regardless of role.</div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', padding: '4px 0' }}>
                        {TEMP_ACCESS_PAGE_OPTIONS.map(opt => {
                          const selected = (form.tempAccessPages || []).includes(opt.key);
                          return (
                            <label key={opt.key} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px' }}>
                              <input
                                type="checkbox"
                                checked={selected}
                                onChange={e => {
                                  const current = Array.isArray(form.tempAccessPages) ? form.tempAccessPages : [];
                                  const next = e.target.checked
                                    ? Array.from(new Set([...current, opt.key]))
                                    : current.filter(k => k !== opt.key);
                                  setForm({ ...form, tempAccessPages: next });
                                }}
                                style={{ width: '14px', height: '14px' }}
                              />
                              <span>{opt.label}</span>
                            </label>
                          );
                        })}
                      </div>
                    </FormRow>
                    <FormRow label="Access Expires" hint="Local end-of-day. Leave empty to revoke all temporary grants.">
                      <input
                        type="date"
                        value={form.tempAccessExpiresAt || ''}
                        onChange={e => setForm({ ...form, tempAccessExpiresAt: e.target.value })}
                        style={{ ...INPUT_STYLE, maxWidth: '180px' }}
                      />
                    </FormRow>
                  </>
                )}

                {/* ── Salary & Level ────────────────────────────── */}
                <FormRow sectionHeader label="Salary & Level" />

                <FormRow label="Job Role" hint="Managed via Role History below">
                  <div style={{ padding: '6px 0', fontSize: '13px', fontWeight: 500, color: 'var(--brio-text, #1e293b)' }}>
                    {form.jobRole || <span style={{ color: 'var(--brio-muted, #94a3b8)', fontStyle: 'italic' }}>Not set — add via Role History</span>}
                  </div>
                </FormRow>

                <FormRow label={(form.officeLocation || 'NL') === 'NL' ? 'Trede' : 'Level'} hint="Managed via Role History below">
                  <div style={{ padding: '6px 0', fontSize: '13px', fontWeight: 500, color: 'var(--brio-text, #1e293b)' }}>
                    {form.jobLevel !== undefined && form.jobLevel !== null ? (() => {
                      const isNL = (form.officeLocation || 'NL') === 'NL';
                      const prefix = isNL ? 'Trede' : 'Level';
                      if (isNL) {
                        const ct = nlCustomTredes.find(t => String(t.id) === String(form.jobLevel));
                        if (ct) return ct.name || `${prefix} ${form.jobLevel}`;
                      }
                      return `${prefix} ${form.jobLevel}`;
                    })() : <span style={{ color: 'var(--brio-muted, #94a3b8)', fontStyle: 'italic' }}>Not set</span>}
                  </div>
                </FormRow>

                {/* ── Quarterly Bonuses ─────────────────────────── */}
                <FormRow sectionHeader label="Quarterly Bonuses" />

                <FormRow label="Year">
                  <select
                    value={form.bonusYear || new Date().getFullYear()}
                    onChange={e => {
                      const year = parseInt(e.target.value);
                      const user = activeUsers.find(u => u.id === form.editUserId);
                      const yearBonuses = user?.bonuses?.[year] || { q1: 0, q2: 0, q3: 0, q4: 0 };
                      const totalManual = (yearBonuses.q1 || 0) + (yearBonuses.q2 || 0) + (yearBonuses.q3 || 0) + (yearBonuses.q4 || 0);
                      let q1 = yearBonuses.q1 || '', q2 = yearBonuses.q2 || '', q3 = yearBonuses.q3 || '', q4 = yearBonuses.q4 || '';
                      if (totalManual === 0 && editModalLedgerBonus[year] > 0) {
                        const perQ = Math.round(editModalLedgerBonus[year] / 4);
                        q1 = q2 = q3 = q4 = perQ;
                      }
                      setForm({ ...form, bonusYear: year, bonusQ1: q1, bonusQ2: q2, bonusQ3: q3, bonusQ4: q4 });
                    }}
                    style={{ ...SELECT_STYLE, maxWidth: '120px' }}
                  >
                    {Array.from({ length: 10 }, (_, i) => {
                      const year = new Date().getFullYear() - 2 + i;
                      return <option key={year} value={year}>{year}</option>;
                    })}
                  </select>
                </FormRow>

                <FormRow label="Q1 / Q2">
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input type="number" value={form.bonusQ1 !== undefined ? form.bonusQ1 : ''} onChange={e => setForm({ ...form, bonusQ1: e.target.value })} placeholder="Q1 (₹)" step="100" style={{ ...INPUT_STYLE, maxWidth: '140px' }} />
                    <input type="number" value={form.bonusQ2 !== undefined ? form.bonusQ2 : ''} onChange={e => setForm({ ...form, bonusQ2: e.target.value })} placeholder="Q2 (₹)" step="100" style={{ ...INPUT_STYLE, maxWidth: '140px' }} />
                  </div>
                </FormRow>

                <FormRow label="Q3 / Q4">
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input type="number" value={form.bonusQ3 !== undefined ? form.bonusQ3 : ''} onChange={e => setForm({ ...form, bonusQ3: e.target.value })} placeholder="Q3 (₹)" step="100" style={{ ...INPUT_STYLE, maxWidth: '140px' }} />
                    <input type="number" value={form.bonusQ4 !== undefined ? form.bonusQ4 : ''} onChange={e => setForm({ ...form, bonusQ4: e.target.value })} placeholder="Q4 (₹)" step="100" style={{ ...INPUT_STYLE, maxWidth: '140px' }} />
                  </div>
                </FormRow>

                {/* Bonus summary */}
                {(() => {
                  const q1 = parseFloat(form.bonusQ1) || 0;
                  const q2 = parseFloat(form.bonusQ2) || 0;
                  const q3 = parseFloat(form.bonusQ3) || 0;
                  const q4 = parseFloat(form.bonusQ4) || 0;
                  const total = q1 + q2 + q3 + q4;
                  const bonusYear = form.bonusYear || new Date().getFullYear();
                  const user = activeUsers.find(u => u.id === form.editUserId);
                  const manualTotal = Object.values(user?.bonuses?.[bonusYear] || {}).reduce((s, v) => s + (v || 0), 0);
                  const isFromLedger = total > 0 && manualTotal === 0 && editModalLedgerBonus[bonusYear] > 0;
                  if (total <= 0) return null;
                  return (
                    <FormRow label="Total">
                      <div style={{ padding: '6px 0', fontWeight: 600, fontSize: '14px', color: 'var(--brio-gold, #9F902A)' }}>
                        ₹{total.toLocaleString()}
                        <span style={{ fontSize: '11px', fontWeight: 400, color: 'var(--brio-muted, #94a3b8)', marginLeft: '8px' }}>
                          (₹{Math.round(total / 12).toLocaleString()}/mo)
                        </span>
                        {isFromLedger && (
                          <span style={{ display: 'block', fontSize: '10px', color: 'var(--brio-muted, #94a3b8)', fontStyle: 'italic', marginTop: '2px' }}>
                            From salary ledger — edit to override
                          </span>
                        )}
                      </div>
                    </FormRow>
                  );
                })()}

                {/* ── Cost-Affecting Changes Warning ────────────── */}
                {(() => {
                  const changes = getEditCostChanges();
                  if (!changes) return null;
                  const { originalUser, bonusYear, originalBonuses, newBonuses, bonusChanged } = changes;
                  return (
                    <>
                      <FormRow sectionHeader label="⚠️ Cost-Affecting Changes" />
                      <tr>
                        <td colSpan="2" style={{ padding: '8px 12px' }}>
                          <div style={{ background: '#fefce8', border: '1px solid #fde68a', borderRadius: '6px', padding: '10px 12px', fontSize: '12px', color: '#854d0e' }}>
                            <div style={{ fontWeight: 600, marginBottom: '6px' }}>Changes detected — please specify effective date:</div>
                            <div style={{ display: 'flex', gap: '8px', marginBottom: '6px', flexWrap: 'wrap' }}>
                              <BrioDatePicker
                                value={form.profileEffectiveDate || new Date().toISOString().split('T')[0]}
                                onChange={v => setForm({ ...form, profileEffectiveDate: v })}
                                style={{ maxWidth: '180px' }}
                              />
                              <input
                                type="text"
                                value={form.profileChangeReason || ''}
                                onChange={e => setForm({ ...form, profileChangeReason: e.target.value })}
                                placeholder="Reason (e.g., Promotion)"
                                style={{ ...INPUT_STYLE, flex: 1, minWidth: '160px', fontSize: '12px' }}
                              />
                            </div>
                            <div style={{ lineHeight: '1.6' }}>
                              {bonusChanged && (
                                <div>• Bonuses ({bonusYear}): ₹{(originalBonuses.q1 + originalBonuses.q2 + originalBonuses.q3 + originalBonuses.q4).toLocaleString()} → ₹{(newBonuses.q1 + newBonuses.q2 + newBonuses.q3 + newBonuses.q4).toLocaleString()}</div>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    </>
                  );
                })()}

                {/* ── Role History Timeline ─────────────────────── */}
                {(() => {
                  const user = activeUsers.find(u => u.id === form.editUserId);
                  return (
                    <RoleHistoryEditor
                      userId={form.editUserId}
                      user={user}
                      jobRoles={jobRoles}
                      nlCustomRoles={nlCustomRoles}
                      nlCustomRoleSchaalMap={nlCustomRoleSchaalMap}
                      nlCustomTredes={nlCustomTredes}
                      isAdmin={isAdmin}
                      activeProjects={activeProjects}
                      activeTasks={activeTasks}
                    />
                  );
                })()}

                {/* ── History (read-only, collapsible) ──────────── */}
                {(() => {
                  const user = activeUsers.find(u => u.id === form.editUserId);
                  const hasSalaryHistory = user?.salaryHistory && user.salaryHistory.length > 0;
                  const hasProfileHistory = user?.profileHistory && user.profileHistory.length > 0;
                  if (!hasSalaryHistory && !hasProfileHistory) return null;
                  return (
                    <>
                      <FormRow sectionHeader label="History" />
                      {hasSalaryHistory && (
                        <tr>
                          <td colSpan="2" style={{ padding: '4px 12px' }}>
                            <details>
                              <summary style={{ fontSize: '12px', fontWeight: 500, color: 'var(--brio-text, #475569)', cursor: 'pointer', padding: '4px 0' }}>
                                📜 Salary History ({user.salaryHistory.length} revisions)
                              </summary>
                              <div style={{ marginTop: '4px', paddingLeft: '12px', borderLeft: '2px solid var(--brio-border, #e2e8f0)' }}>
                                {user.salaryHistory.sort((a, b) => b.effectiveDate.localeCompare(a.effectiveDate)).map((h, idx) => (
                                  <div key={idx} style={{ fontSize: '11px', color: 'var(--brio-muted, #64748b)', padding: '2px 0' }}>
                                    <span style={{ fontWeight: 500 }}>{h.effectiveDate}</span>: ₹{h.salary.toLocaleString()}
                                    {h.reason && <span style={{ opacity: 0.7 }}> – {h.reason}</span>}
                                  </div>
                                ))}
                              </div>
                            </details>
                          </td>
                        </tr>
                      )}
                      {hasProfileHistory && (
                        <tr>
                          <td colSpan="2" style={{ padding: '4px 12px' }}>
                            <details>
                              <summary style={{ fontSize: '12px', fontWeight: 500, color: 'var(--brio-text, #475569)', cursor: 'pointer', padding: '4px 0' }}>
                                📋 Profile History ({user.profileHistory.length} changes)
                              </summary>
                              <div style={{ marginTop: '4px', paddingLeft: '12px', borderLeft: '2px solid var(--brio-border, #e2e8f0)' }}>
                                {user.profileHistory.sort((a, b) => b.effectiveDate.localeCompare(a.effectiveDate)).map((h, idx) => (
                                  <div key={idx} style={{ fontSize: '11px', color: 'var(--brio-muted, #64748b)', padding: '3px 0', borderBottom: '1px solid var(--brio-border, #f1f5f9)' }}>
                                    <div style={{ fontWeight: 500 }}>
                                      {h.effectiveDate}
                                      {h.changeReason && <span style={{ fontWeight: 400, opacity: 0.7 }}> – {h.changeReason}</span>}
                                    </div>
                                    <div style={{ paddingLeft: '8px' }}>
                                      {Object.keys(h.changes).map(field => {
                                        const change = h.changes[field];
                                        return <div key={field}>• {field}: {change.from !== null ? change.from : 'None'} → {change.to}</div>;
                                      })}
                                    </div>
                                    {h.changedBy && <div style={{ opacity: 0.5, marginTop: '2px' }}>Changed by: {h.changedBy}</div>}
                                  </div>
                                ))}
                              </div>
                            </details>
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })()}

              </tbody>
            </BrioTable>
          </BrioByCode>
        </BrioModal>
      )}

      {/* ================================================================== */}
      {/* LINK LOGIN MODAL - Admin only                                      */}
      {/* ================================================================== */}
      {showLinkLoginModal && isAdmin && (
        <BrioModal
          title="Link Login to Employee"
          onClose={() => setShowLinkLoginModal(false)}
          width="md"
          footer={
            <BrioActionGroup align="end">
              <button onClick={() => setShowLinkLoginModal(false)} className="brio-btn brio-btn-secondary" disabled={linkLoginLoading}>Cancel</button>
              <button onClick={handleSaveLinkLogin} className="brio-btn brio-btn-primary" disabled={!canSave || linkLoginLoading}>
                {linkLoginLoading ? 'Saving...' : 'Save'}
              </button>
            </BrioActionGroup>
          }
        >
          <div className="space-y-4">
            {linkLoginSuccess && (
              <div style={{ padding: '12px 16px', backgroundColor: '#dcfce7', border: '1px solid #86efac', borderRadius: '8px', color: '#166534' }}>✓ {linkLoginSuccess}</div>
            )}
            {linkLoginError && (
              <div style={{ padding: '12px 16px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', color: '#991b1b' }}>✗ {linkLoginError}</div>
            )}
            <div style={{ padding: '12px 16px', backgroundColor: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '8px', fontSize: '13px', color: '#0369a1' }}>
              <strong>How to get the Auth User ID:</strong><br/>
              Supabase Dashboard → Authentication → Users → Click user → Copy "User UID"
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Select Employee</label>
              <select value={linkLoginForm.selectedEmployeeId} onChange={(e) => setLinkLoginForm({ ...linkLoginForm, selectedEmployeeId: e.target.value, confirmRelink: false })} className="w-full px-3 py-2 border rounded-lg" disabled={linkLoginLoading}>
                <option value="">-- Select an employee --</option>
                {activeUsers.sort((a, b) => a.name.localeCompare(b.name)).map(u => {
                  const uAuthId = getAuthUserId(u);
                  return <option key={u.id} value={u.id}>{u.name} — {u.email} — ID: {u.id}{uAuthId ? ' (has login)' : ''}</option>;
                })}
              </select>
            </div>
            {employeeAlreadyLinked && (
              <div style={{ padding: '12px 16px', backgroundColor: '#fef3c7', border: '1px solid #fcd34d', borderRadius: '8px', color: '#92400e' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                  <AlertTriangle className="w-5 h-5 flex-shrink-0" style={{ marginTop: '2px' }} />
                  <div>
                    <strong>Warning:</strong> This employee already has a login linked.<br/>
                    <span style={{ fontSize: '12px', opacity: 0.8 }}>Current Auth ID: {selectedEmployeeAuthId}</span>
                  </div>
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '12px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={linkLoginForm.confirmRelink} onChange={(e) => setLinkLoginForm({...linkLoginForm, confirmRelink: e.target.checked})} disabled={linkLoginLoading} />
                  <span style={{ fontSize: '13px' }}>I understand this will re-link the login</span>
                </label>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium mb-1">Auth User ID (UUID)</label>
              <input type="text" value={linkLoginForm.authUserId} onChange={(e) => setLinkLoginForm({...linkLoginForm, authUserId: e.target.value})} placeholder="e.g., 123e4567-e89b-12d3-a456-426614174000" className="w-full px-3 py-2 border rounded-lg font-mono text-sm" disabled={linkLoginLoading} style={{ borderColor: linkLoginForm.authUserId && !isValidUUID(linkLoginForm.authUserId) ? '#ef4444' : undefined }} />
              {linkLoginForm.authUserId && !isValidUUID(linkLoginForm.authUserId) && (
                <p className="text-xs mt-1" style={{ color: '#ef4444' }}>Invalid UUID format. Expected: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx</p>
              )}
            </div>
            {uuidAlreadyLinkedTo && (
              <div style={{ padding: '12px 16px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', color: '#991b1b' }}>
                <strong>Cannot link:</strong> This login is already linked to <strong>{uuidAlreadyLinkedTo.name}</strong> ({uuidAlreadyLinkedTo.email}).<br/>
                <span style={{ fontSize: '12px', opacity: 0.8 }}>Each Auth account can only be linked to one employee.</span>
              </div>
            )}
          </div>
        </BrioModal>
      )}

      {/* ================================================================== */}
      {/* CREATE USER MODAL - Admin only (via Edge Function)                 */}
      {/* ================================================================== */}
      {showCreateUserModal && isAdmin && (
        <BrioModal
          title="Invite New User"
          onClose={() => setShowCreateUserModal(false)}
          width="md"
          footer={
            <BrioActionGroup align="end">
              <button onClick={() => setShowCreateUserModal(false)} className="brio-btn brio-btn-secondary" disabled={createUserLoading}>Cancel</button>
              <button onClick={handleCreateUser} className="brio-btn brio-btn-primary" disabled={createUserLoading || !createUserForm.name || !createUserForm.email || !createUserForm.tempPassword}>
                {createUserLoading ? 'Creating...' : 'Create & Invite'}
              </button>
            </BrioActionGroup>
          }
        >
          <div className="space-y-4">
            {createUserSuccess && (
              <div style={{ padding: '12px 16px', backgroundColor: '#dcfce7', border: '1px solid #86efac', borderRadius: '8px', color: '#166534' }}>✓ {createUserSuccess}</div>
            )}
            {createUserError && (
              <div style={{ padding: '12px 16px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', color: '#991b1b' }}>✗ {createUserError}</div>
            )}
            <div style={{ padding: '12px 16px', backgroundColor: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '8px', fontSize: '13px', color: '#0369a1' }}>
              <strong>This will:</strong><br/>
              1. Create a Supabase Auth account<br/>
              2. Link it to a new or existing employee record<br/>
              3. Set status to "invited" (activates on first login)
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Name *</label>
              <input type="text" value={createUserForm.name} onChange={(e) => setCreateUserForm({...createUserForm, name: e.target.value})} placeholder="Full name" className="w-full px-3 py-2 border rounded-lg" disabled={createUserLoading} />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Email *</label>
              <input type="email" value={createUserForm.email} onChange={(e) => setCreateUserForm({...createUserForm, email: e.target.value})} placeholder="user@example.com" className="w-full px-3 py-2 border rounded-lg" disabled={createUserLoading} />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Role *</label>
              <select value={createUserForm.role} onChange={(e) => setCreateUserForm({...createUserForm, role: e.target.value})} className="w-full px-3 py-2 border rounded-lg" disabled={createUserLoading}>
                <option value="user">User</option>
                <option value="supervisor">Supervisor</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Team</label>
              <select value={createUserForm.teamId} onChange={(e) => setCreateUserForm({...createUserForm, teamId: e.target.value})} className="w-full px-3 py-2 border rounded-lg" disabled={createUserLoading}>
                <option value="">-- No team --</option>
                {activeTeams.sort((a, b) => a.name.localeCompare(b.name)).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Temporary Password *</label>
              <input type="text" value={createUserForm.tempPassword} onChange={(e) => setCreateUserForm({...createUserForm, tempPassword: e.target.value})} placeholder="Min 6 characters" className="w-full px-3 py-2 border rounded-lg" disabled={createUserLoading} />
              <p className="text-xs text-slate-500 mt-1">Share this password with the user. They should change it after first login.</p>
            </div>
          </div>
        </BrioModal>
      )}

      {/* ================================================================== */}
      {/* RESET PASSWORD MODAL - Admin only (via Edge Function)              */}
      {/* ================================================================== */}
      {showResetPasswordModal && isAdmin && (
        <BrioModal
          title="Reset Password"
          onClose={() => setShowResetPasswordModal(false)}
          width="sm"
          footer={
            <BrioActionGroup align="end">
              <button onClick={() => setShowResetPasswordModal(false)} className="brio-btn brio-btn-secondary" disabled={resetPasswordLoading}>Cancel</button>
              <button onClick={handleResetPassword} className="brio-btn brio-btn-primary" disabled={resetPasswordLoading}>
                {resetPasswordLoading ? 'Sending...' : (resetPasswordForm.userStatus === 'invited' ? 'Resend Setup Email' : 'Send Reset Email')}
              </button>
            </BrioActionGroup>
          }
        >
          <div className="space-y-4">
            {resetPasswordSuccess && (
              <div style={{ padding: '12px 16px', backgroundColor: '#dcfce7', border: '1px solid #86efac', borderRadius: '8px', color: '#166534' }}>✓ {resetPasswordSuccess}</div>
            )}
            {resetPasswordError && (
              <div style={{ padding: '12px 16px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', color: '#991b1b' }}>✗ {resetPasswordError}</div>
            )}
            <div style={{ padding: '12px 16px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
              <div className="text-sm"><strong>User:</strong> {resetPasswordForm.userName}</div>
              <div className="text-sm text-slate-600">{resetPasswordForm.userEmail}</div>
            </div>
            <p className="text-sm text-slate-600">
              {resetPasswordForm.userStatus === 'invited'
                ? 'This will resend the account setup email. The user can click the link to set their password.'
                : 'This will send a password reset email. The user can click the link to set a new password.'}
            </p>
          </div>
        </BrioModal>
      )}

      {/* ================================================================== */}
      {/* FULLSCREEN TEAM MEMBER DETAIL OVERLAY                             */}
      {/* ================================================================== */}
      {isTeamMemberFullscreenOpen && selectedFullscreenUserId && (
        <TeamMemberFullScreenPage
          userId={selectedFullscreenUserId}
          onClose={closeFullscreen}
          currentUser={currentUser}
          memberUser={fsSelectedMember}
          supabaseUsers={supabaseUsers}
          activeTeams={activeTeams}
          activeTimeEntries={activeTimeEntries}
          activeProjects={activeProjects}
          getInternalCostRate={getInternalCostRate}
          supervisorIsInMembersTeam={fsSupervisorIsInMembersTeam}
        />
      )}
    </div>
  );
}
